import type { ImageEdit } from '@/types/shared'
import { getApiUrl } from './serverConfig'
import { UploadTransferError } from './uploadTransferError'
import { uint8ToBase64 } from './base64'
import { getAccessToken } from './auth'
import { authenticatedRequest as fetchWithAuth } from './authenticatedRequest'
import type {
  ModelInfo,
  GenerationParams,
  Job,
  ShotSpec,
  JobMetadata,
  UpscalePreset,
  UpscalePresetType,
  McpAgentPresence,
  FalCatalogItem,
  SubjectCropRect,
  StillImageThumbnailVariants,
  ProjectModelPricingContext,
  Asset,
} from '@/types'
import { captureSentryError } from './sentry'
import { rewriteLegacyStorageHost } from './legacyStorageHost'
import { normalizeImageDataUrl } from './uploadImage'
import {
  cardBillsVideoReferences,
  resolveCustomerPricingGuard,
  resolveProjectModelPricing,
} from '@/components/generation/shared/hooks/useCostEstimation'
import { BackendRouteUnavailableError } from './apiErrors'
import { getAssetProjectContext } from './assetProjectContext'
import type { DeepCopyIdMaps } from './multiplayer/remapProjectIds'
import type { ReferralSource } from './signupAccessStorage'
import type {
  TeamCheckoutQuote,
  ColorLabelNames,
  ShotColorLabel,
  AutoTopupConfig,
  NormalizedInvoice,
  OrganizationRole,
  SpendAnalyticsResponse,
  SpendBucket,
  SpendGroupBy,
  MemberSpendCapsResponse,
  McpUsageResponse,
  CommentRecord,
  CreateCommentRequest,
} from '@/types/shared'
import type { AppLocale } from '@/i18n/routing'

/**
 * API client for backend communication
 * Uses dynamic API URL based on current server selection
 */

// Asset spec and job metadata caches (immutable after generation)
const assetSpecCache = new Map<string, ShotSpec | null>()
const assetJobCache = new Map<string, JobMetadata | null>()

/**
 * Verify that frontend and backend are connected to the same Supabase instance.
 * This prevents authentication errors when URLs don't match.
 *
 * For staging with Supabase branching: When backend has ALLOW_BRANCH_URLS=true,
 * we compare by project ref instead of exact URL match.
 */
let supabaseUrlCheckPerformed = false

/**
 * Extract project ref from Supabase URL.
 * Handles both main project URLs and branch URLs.
 *
 * Examples:
 * - https://qccjhofczngztnljyqqr.supabase.co → qccjhofczngztnljyqqr
 * - https://qccjhofczngztnljyqqr-abc123.supabase.co → qccjhofczngztnljyqqr
 */
function extractSupabaseProjectRef(url: string): string {
  const match = url.match(/https:\/\/([a-z]+)(?:-[a-z0-9]+)?\.supabase\.co/)
  return match?.[1] ?? ''
}

export async function verifySupabaseUrlMatch(): Promise<void> {
  // Only check once per session
  if (supabaseUrlCheckPerformed) {
    return
  }

  // Local phone-capture tunnel (scripts/capture-tunnel.mjs): the frontend reaches
  // Supabase through the single-origin proxy (https://<tunnel>/supabase) while the
  // backend talks to it directly (127.0.0.1:54321). Different URLs, SAME instance,
  // so the exact-match check below would false-positive and block the app. Only
  // ever set in the gitignored .env.capture, so normal dev/deploys are unaffected.
  if (process.env.NEXT_PUBLIC_CAPTURE_TUNNEL === '1') {
    supabaseUrlCheckPerformed = true
    return
  }

  const isProduction = process.env.NODE_ENV === 'production'
  const frontendUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  if (!frontendUrl) {
    console.warn('[API] NEXT_PUBLIC_SUPABASE_URL not set - skipping Supabase URL verification')
    return
  }

  console.log(`[API] Verifying Supabase URL match...`)
  console.log(`[API] Frontend URL: ${frontendUrl}`)

  // Helper to handle errors: in production log to Sentry but don't block, in dev throw
  const handleError = (error: Error, extra: Record<string, unknown>) => {
    captureSentryError(error, {
      operation: 'supabase_url_verify',
      extra,
    })
    if (isProduction) {
      // In production, mark as checked to avoid retrying (config issues are persistent)
      supabaseUrlCheckPerformed = true
      return
    }
    throw error
  }

  try {
    const apiUrl = getApiUrl()
    console.log(`[API] Fetching backend config from: ${apiUrl}/config`)
    const res = await fetch(`${apiUrl}/config`)

    if (!res.ok) {
      const errorText = await res.text()
      console.error(`[API] Failed to fetch backend config (${res.status}): ${errorText}`)
      const error = new Error(
        `Failed to verify Supabase URL: Backend config endpoint returned ${res.status}. ` +
          `Please ensure the backend is running and accessible.`,
      )
      return handleError(error, { frontendUrl, apiUrl, status: res.status })
    }

    const config = await res.json()
    const backendUrl = config.supabaseUrl
    console.log(`[API] Backend URL: ${backendUrl || '(not configured)'}`)

    if (!backendUrl) {
      const error = new Error(`Backend Supabase URL not configured. Please set SUPABASE_URL in backend/.env`)
      return handleError(error, { frontendUrl, apiUrl })
    }

    // Branch mode: compare project refs instead of exact URLs
    // This enables Supabase branching where each PR gets a unique branch URL
    if (config.allowBranchUrls && config.supabaseProjectRef) {
      const frontendRef = extractSupabaseProjectRef(frontendUrl)
      console.log(`[API] Branch mode - comparing project refs: "${frontendRef}" vs "${config.supabaseProjectRef}"`)

      if (frontendRef !== config.supabaseProjectRef) {
        const error = new Error(
          `❌ Supabase Project Mismatch!\n\n` +
            `Frontend project ref: ${frontendRef}\n` +
            `Backend project ref:  ${config.supabaseProjectRef}\n\n` +
            `Frontend and backend are connected to different Supabase projects.`,
        )
        console.error('[API]', error.message)
        return handleError(error, { frontendUrl, frontendRef, backendRef: config.supabaseProjectRef, apiUrl })
      }

      supabaseUrlCheckPerformed = true
      console.log(`[API] ✓ Supabase project verified (branch mode): ${frontendRef}`)
      return
    }

    // Normal mode: exact URL match
    // Normalize URLs for comparison (remove trailing slashes)
    const normalizedFrontend = frontendUrl.replace(/\/$/, '')
    const normalizedBackend = backendUrl.replace(/\/$/, '')

    console.log(`[API] Comparing: "${normalizedFrontend}" vs "${normalizedBackend}"`)

    if (normalizedFrontend !== normalizedBackend) {
      const error = new Error(
        `❌ Supabase URL Mismatch!\n\n` +
          `Frontend: ${normalizedFrontend}\n` +
          `Backend:  ${normalizedBackend}\n\n` +
          `This will cause authentication failures. Please ensure both frontend and backend use the same Supabase instance.\n\n` +
          `Frontend config: NEXT_PUBLIC_SUPABASE_URL in .env.local or .env.staging\n` +
          `Backend config: SUPABASE_URL in backend/.env`,
      )
      console.error('[API]', error.message)
      return handleError(error, { frontendUrl: normalizedFrontend, backendUrl: normalizedBackend, apiUrl })
    }

    supabaseUrlCheckPerformed = true
    console.log(`[API] ✓ Supabase URL verified: ${normalizedFrontend} (frontend and backend match)`)
  } catch (error) {
    // Handle unexpected errors (network failures, JSON parse errors, etc.)
    const wrappedError =
      error instanceof Error ? error : new Error(`Failed to verify Supabase URL match: ${String(error)}`)

    // Use handleError for consistent behavior in prod vs dev
    return handleError(wrappedError, {
      frontendUrl: process.env.NEXT_PUBLIC_SUPABASE_URL,
      apiUrl: getApiUrl(),
    })
  }
}

// Helper to get current API URL (called on each request for dynamic switching)
const apiUrl = () => getApiUrl()

/**
 * Fetch all available models
 */
export async function fetchModels(
  capability?: 'generate-video' | 'extend-video' | 'edit-video' | 'generate-still' | 'generate-audio' | 'inpaint-audio',
  options?: { projectId?: string },
): Promise<{
  models: ModelInfo[]
  defaultModelId?: string
  orgDisabledModelIds?: string[]
  orgDisabledResolutionsByModel?: Record<string, string[]>
  projectPricing?: ProjectModelPricingContext
}> {
  const params = new URLSearchParams()
  if (capability) params.set('capability', capability)
  if (options?.projectId) params.set('projectId', options.projectId)
  const qs = params.toString()
  const url = qs ? `${apiUrl()}/models?${qs}` : `${apiUrl()}/models`
  const res = await fetchWithAuth(url)

  if (!res.ok) {
    throw new Error(`Failed to fetch models: ${res.statusText}`)
  }

  const data = await res.json()
  return {
    models: data.models,
    defaultModelId: data.defaultModelId,
    orgDisabledModelIds: data.orgDisabledModelIds,
    orgDisabledResolutionsByModel: data.orgDisabledResolutionsByModel,
    projectPricing: data.projectPricing,
  }
}

/**
 * Fetch a specific model by ID
 */
export async function fetchModel(modelId: string): Promise<ModelInfo | null> {
  const res = await fetchWithAuth(`${apiUrl()}/models/${modelId}`)

  if (res.status === 404) {
    return null
  }

  if (!res.ok) {
    throw new Error(`Failed to fetch model: ${res.statusText}`)
  }

  return res.json()
}

export async function fetchModelCatalog(): Promise<{
  catalog: FalCatalogItem[]
  requestedIds: string[]
}> {
  const res = await fetchWithAuth(`${apiUrl()}/models/catalog`)
  if (!res.ok) throw new Error(`Failed to fetch model catalog: ${res.statusText}`)
  return res.json()
}

export async function submitModelRequest(data: {
  catalogModelId?: string
  modelName?: string
  message?: string
}): Promise<{ success: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/models/request`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error(`Failed to submit model request: ${res.statusText}`)
  return res.json()
}

// Re-export FrameUrls from shared types for API consumers
export type { FrameUrls } from '@/types/shared'
import type { FrameUrls } from '@/types/shared'

/**
 * Upload a frame image before starting generation.
 *
 * This allows batch generation to upload once and reuse URLs for all jobs,
 * rather than uploading 4x for a batch of 4.
 *
 * NOTE: This uses a simpler pattern than video upload (initUpload/uploadToR2/completeUpload)
 * because frames are small (1-2MB) and don't need presigned URLs or transcoding.
 *
 * @param dataUrl - Base64 data URL of the image
 * @returns Object with the R2 URL and asset ID
 */
export async function uploadFrame(dataUrl: string): Promise<{ url: string; assetId: string; thumbnailUrl?: string }> {
  // Normalize AVIF data URLs to JPEG/PNG so the backend (and downstream
  // providers) only ever see provider-friendly formats. No-op otherwise.
  const normalizedDataUrl = await normalizeImageDataUrl(dataUrl)
  const res = await fetchWithAuth(`${apiUrl()}/upload/frame`, {
    method: 'POST',
    body: JSON.stringify({ dataUrl: normalizedDataUrl }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Frame upload failed')
  }

  return res.json()
}

export interface CameraSceneStatus {
  sceneId: string
  status: 'processing' | 'ready' | 'failed'
  sceneUrl?: string
  meta?: {
    width: number
    height: number
    pointCount: number
    fovYDeg: number
    near: number
    far: number
    sceneFormat?: string
    confidenceTau?: number
    depthPasses?: number
  }
  error?: string
}

export async function createCameraScene(
  projectId: string,
  imageUrl: string,
  options?: { signal?: AbortSignal },
): Promise<CameraSceneStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/camera-control/scenes`, {
    method: 'POST',
    body: JSON.stringify({ projectId, imageUrl }),
    signal: options?.signal,
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to prepare the scene')
  }

  return res.json()
}

export async function getCameraScene(
  sceneId: string,
  projectId: string,
  options?: { signal?: AbortSignal },
): Promise<CameraSceneStatus> {
  const params = new URLSearchParams({ projectId })
  const res = await fetchWithAuth(`${apiUrl()}/camera-control/scenes/${sceneId}?${params.toString()}`, {
    signal: options?.signal,
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to read the scene')
  }

  return res.json()
}

/** A board Subject as returned by GET /board/:projectId/subjects. */
export interface MotionSubjectSummary {
  id: string
  name: string
  origin?: 'character' | 'element'
  category?: string
  thumbnailUrl?: string
}

/**
 * Motion onboarding (Plan A): start auto-extracting the cast/locations in the
 * chosen image. Fire-and-forget (backend returns 202). Legacy delivery is
 * reusable board Subjects (read via getBoardSubjects); `libraryPath: true`
 * means the results land as library collections + plain canvas image nodes
 * instead (read via the source asset's cameraMotion.subjectExtraction state).
 */
export async function extractMotionSubjects(args: {
  projectId: string
  sourceAssetId: string
  imageUrl: string
  sceneId: string
}): Promise<{ libraryPath: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/camera-control/extract-subjects`, {
    method: 'POST',
    body: JSON.stringify(args),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to start subject extraction')
  }
  const body = (await res.json().catch(() => ({}))) as { libraryPath?: boolean }
  return { libraryPath: Boolean(body.libraryPath) }
}

/** Read the project's board Subjects (durable source of truth for the reveal). */
export async function getBoardSubjects(
  projectId: string,
  sceneId?: string,
): Promise<{ subjects: MotionSubjectSummary[] }> {
  const query = sceneId ? `?${new URLSearchParams({ sceneId }).toString()}` : ''
  const res = await fetchWithAuth(`${apiUrl()}/board/${projectId}/subjects${query}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to read subjects')
  }
  return res.json()
}

/** Motion onboarding (Plan A): write the cohort/lifecycle row (flow='motion' +
 *  step) so the cohort is measurable and the house-funded render entitlement
 *  row exists. Best-effort from the client. */
export async function startMotionFlow(
  projectId: string,
  step: 'image' | 'fly' | 'rendering' | 'reveal' = 'image',
): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/camera-control/motion/start`, {
    method: 'POST',
    body: JSON.stringify({ projectId, step }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to start motion onboarding')
  }
}

export interface OnboardingMotionState {
  flow: string | null
  motion_status: string | null
  motion_step: string | null
  drafted_project_id: string | null
  motion_render_fired_at: string | null
}

/** Read the durable motion-onboarding lifecycle row, when one exists. */
export async function getMotionFlowStatus(): Promise<{ state: OnboardingMotionState | null }> {
  const res = await fetchWithAuth(`${apiUrl()}/camera-control/motion/status`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to read motion onboarding status')
  }
  return res.json()
}

/** Motion onboarding (Plan A): fire the HOUSE-FUNDED (free) render.
 *  Throws on non-OK (e.g. 409 ALREADY_FIRED); callers must surface the failure
 *  and require an explicit user action before any paid render. */
export async function fireMotionRender(
  projectId: string,
  assetId: string,
  /** Optional verbatim direction note from the onboarding "direction" step. */
  notes?: string,
): Promise<{ jobId: string; assetId: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/camera-control/motion/render`, {
    method: 'POST',
    body: JSON.stringify({ projectId, assetId, ...(notes ? { notes } : {}) }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to start house-funded render')
  }
  return res.json()
}

export async function createCameraMotionGuide(args: {
  draftAssetId: string
  projectId: string
  metadata: Record<string, unknown>
  userVisibleGuideFile?: Blob
  finalFrameFile?: Blob
}): Promise<{
  draftAssetId: string
  userVisibleGuideVideoUrl?: string
  finalFrameUrl: string
  finalFrameThumbnailUrl?: string
  metadataUrl: string
  durationSeconds?: number
  fps?: 24
  aspectRatio?: string
  width?: number
  height?: number
}> {
  const token = await getAccessToken()
  if (!token) {
    throw new Error('Authentication required')
  }

  const hasDisplayArtifacts = Boolean(args.userVisibleGuideFile || args.finalFrameFile)
  const res = hasDisplayArtifacts
    ? await (() => {
        const formData = new FormData()
        formData.append('draftAssetId', args.draftAssetId)
        formData.append('metadata', JSON.stringify(args.metadata))
        if (args.userVisibleGuideFile) {
          formData.append('file', args.userVisibleGuideFile, 'camera-motion-display-guide.webm')
        }
        if (args.finalFrameFile) {
          formData.append('finalFrame', args.finalFrameFile, 'camera-motion-final-frame.jpg')
        }
        return fetch(`${apiUrl()}/projects/${args.projectId}/camera-motion/guide`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        })
      })()
    : await fetch(`${apiUrl()}/projects/${args.projectId}/camera-motion/guide`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          draftAssetId: args.draftAssetId,
          ...args.metadata,
        }),
      })

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    // Prefer the route's `error` field, then any generic `message` (e.g.
    // requireRealAccount returns { code, message } with no `error`, which would
    // otherwise mask the real cause behind the generic fallback).
    throw new CameraMotionGuideError(error.error || error.message || 'Failed to create camera motion guide', error)
  }

  return res.json()
}

export async function createCameraControlMotion(args: {
  projectId: string
  assetId: string
  metadata: Record<string, unknown>
}): Promise<{ motion: Record<string, unknown> }> {
  const token = await getAccessToken()
  if (!token) {
    throw new Error('Authentication required')
  }

  const res = await fetch(`${apiUrl()}/projects/${args.projectId}/camera-motion/motions`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      assetId: args.assetId,
      ...args.metadata,
    }),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to save camera motion')
  }

  return res.json()
}

/**
 * Publish (or unpublish) a rendered motion to the public /m/<id> share page.
 * Returns the absolute share URL when published. Throws with a MOTION_NOT_RENDERED
 * code in the message when the render isn't finished.
 */
export async function setCameraMotionPublished(args: {
  projectId: string
  assetId: string
  published: boolean
  creditName?: string | null
}): Promise<{ motion: { id: string; publishedAt: string | null }; shareUrl: string | null }> {
  const token = await getAccessToken()
  if (!token) {
    throw new Error('Authentication required')
  }

  const res = await fetch(`${apiUrl()}/projects/${args.projectId}/camera-motion/motions/${args.assetId}/publish`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ published: args.published, creditName: args.creditName ?? undefined }),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(
      error.code === 'MOTION_NOT_RENDERED' ? 'MOTION_NOT_RENDERED' : error.error || 'Failed to publish motion',
    )
  }

  return res.json()
}

export class CameraMotionGuideError extends Error {
  stage?: string
  errorCode?: string
  requestId?: string
  draftAssetId?: string
  guideRevisionId?: string
  retryable?: boolean

  constructor(message: string, payload: unknown) {
    super(message)
    this.name = 'CameraMotionGuideError'
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return
    const data = payload as Record<string, unknown>
    this.stage = typeof data.stage === 'string' ? data.stage : undefined
    this.errorCode = typeof data.errorCode === 'string' ? data.errorCode : undefined
    this.requestId = typeof data.requestId === 'string' ? data.requestId : undefined
    this.draftAssetId = typeof data.draftAssetId === 'string' ? data.draftAssetId : undefined
    this.guideRevisionId = typeof data.guideRevisionId === 'string' ? data.guideRevisionId : undefined
    this.retryable = typeof data.retryable === 'boolean' ? data.retryable : undefined
  }
}

/**
 * Upload a flattened image-markup result as a PERMANENT, CORS-enabled canvas
 * asset (the `assets/{id}/...` path), with a generated thumbnail ladder.
 *
 * Use this instead of {@link uploadFrame} when the result becomes durable canvas
 * node media: `uploadFrame` writes to the transient, non-CORS `tmp/` folder,
 * which black-screens the WebGL canvas texture and may be garbage-collected.
 */
export async function uploadMarkupImage(dataUrl: string): Promise<{
  url: string
  assetId: string
  thumbnailUrl?: string
  thumbnailVariants?: StillImageThumbnailVariants
  thumbnailRevisionId?: string
  width?: number
  height?: number
}> {
  const normalizedDataUrl = await normalizeImageDataUrl(dataUrl)
  const res = await fetchWithAuth(`${apiUrl()}/upload/markup`, {
    method: 'POST',
    body: JSON.stringify({ dataUrl: normalizedDataUrl }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Markup upload failed')
  }

  return res.json()
}

/**
 * Submit user feedback to backend, which forwards to Productlane.
 */
export async function submitFeedback(data: {
  message: string
  category: string
  priority: string
  screenshotUrl?: string
  pageUrl?: string
  userAgent?: string
}): Promise<{ success: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/feedback`, {
    method: 'POST',
    body: JSON.stringify(data),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to submit feedback')
  }

  return res.json()
}

/**
 * The asset has no frame to hand back: no original, no proxy rendition, and no
 * still to stand in for one (ENG-791).
 *
 * An ordinary data state rather than a fault, so callers fall back to their own
 * frame capture without reporting it. `message` is the backend's own sentence
 * for the user — safe to render at a display site.
 */
export class OriginalVideoUnavailableError extends Error {
  readonly code = 'original_video_unavailable'

  constructor(message: string) {
    super(message)
    this.name = 'OriginalVideoUnavailableError'
  }
}

export function isOriginalVideoUnavailableError(error: unknown): error is OriginalVideoUnavailableError {
  return error instanceof OriginalVideoUnavailableError
}

/**
 * Extract a full-resolution frame from the original source video on the backend.
 * Returns an R2 URL of the extracted JPEG frame at the original video's resolution.
 * This replaces proxy-quality frame captures for generation inputs.
 *
 * @param assetId - The asset ID of the video to extract from
 * @param timestamp - Time in seconds to extract the frame at
 * @returns Object with the R2 URL and frame dimensions
 */
export async function extractFullResFrame(
  assetId: string,
  timestamp: number,
  projectId: string,
  storageAssetId?: string,
): Promise<{ url: string; width: number; height: number }> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 35000) // 35s timeout (server may need up to 30s for ffprobe+ffmpeg)

  try {
    const res = await fetchWithAuth(`${apiUrl()}/extract-frame`, {
      method: 'POST',
      body: JSON.stringify({ assetId, timestamp, projectId, storageAssetId }),
      signal: controller.signal,
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({}))
      const message = error.message || error.error || 'Frame extraction failed'
      if (res.status === 404 && error.code === 'original_video_unavailable') {
        throw new OriginalVideoUnavailableError(message)
      }
      throw new Error(message)
    }

    return res.json()
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Crop a completed image asset into one or more derived child assets,
 * re-crop an existing crop child, or restore one of its revisions
 * (contract C16). The server runs a sharp extract against the source image,
 * authors the crop children, and merges them into the project's Yjs doc —
 * the new assets arrive on the board via collaboration sync, not from this
 * response.
 *
 * @param assetId - The completed image asset to crop (create / re-crop),
 *   or the crop child itself when restoring a revision
 * @param params.rects - Crop rects in natural pixels of the source image
 *   (multiple rects = the internal grid presets, one call)
 * @param params.positions - Canvas position per rect (aligned with rects):
 *   the server authors each child at this exact position, so the node the
 *   client pre-placed doesn't jump when the authored crop arrives over sync
 * @param params.replaceAssetId - Re-crop path: update this existing crop
 *   child instead of creating a new one (appends a revision)
 * @param params.restoreRevisionId - Restore path (mutually exclusive with
 *   rects/replaceAssetId): re-point the crop at this revision's EXISTING
 *   file — parent-independent, works even when the source left the board
 */
export async function cropAsset(
  assetId: string,
  params:
    | {
        projectId: string
        rects: SubjectCropRect[]
        positions?: { x: number; y: number }[]
        replaceAssetId?: string
      }
    | { projectId: string; restoreRevisionId: string },
): Promise<{ assets: { assetId: string; uri: string; width: number; height: number }[] }> {
  const res = await fetchWithAuth(`${apiUrl()}/assets/${assetId}/crop`, {
    method: 'POST',
    body: JSON.stringify(params),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || 'Crop failed')
  }

  return res.json()
}

export type EditAssetBody =
  | { projectId: string; action: 'create'; edits: { edit: ImageEdit; position?: { x: number; y: number } }[] }
  | { projectId: string; action: 'update'; patch: Partial<ImageEdit>; expectedRevisionId: string }
  | { projectId: string; action: 'restore'; revisionId: string }

/** Backend-authored user copy, with status kept separate for conflict handling. */
export class AssetEditError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'AssetEditError'
  }
}

export async function editAsset(
  assetId: string,
  body: EditAssetBody,
): Promise<{
  assets: { assetId: string; uri: string; width: number; height: number; revisionId: string }[]
}> {
  const res = await fetchWithAuth(`${apiUrl()}/assets/${assetId}/edit`, { method: 'POST', body: JSON.stringify(body) })
  if (!res.ok) {
    const payload: unknown = await res.json().catch(() => null)
    const message =
      payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string'
        ? payload.error
        : "Couldn't save edit"
    throw new AssetEditError(message, res.status)
  }
  return res.json()
}

/**
 * Auto-select an object via SAM2 from a box and/or point selection.
 *
 * Box/point prompts must be in the SOURCE image's pixel coordinate space. The
 * returned PNG mask has white = the selected object; the markup tool renders it
 * as a visible highlight/outline rather than sending it to a model as a mask.
 */
export async function segmentWithSam2(params: {
  imageUrl: string
  // The backend verifies the caller can read this project and that it owns imageUrl
  // before spending the server FAL key — prevents segmenting cross-project asset URLs.
  projectId: string
  boxPrompts?: Array<{ x_min: number; y_min: number; x_max: number; y_max: number }>
  pointPrompts?: Array<{ x: number; y: number; label: number }>
}): Promise<{ maskUrl: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/segment/sam2`, {
    method: 'POST',
    body: JSON.stringify(params),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || 'Segmentation failed')
  }

  return res.json()
}

/**
 * Initialize a voice audio upload (get presigned URL for direct R2 upload).
 */
export async function uploadVoiceInit(
  filename: string,
  contentType: string,
  sizeBytes: number,
  projectId: string,
): Promise<{ uploadUrl: string; voiceAssetUrl: string; assetId: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/upload/voice`, {
    method: 'POST',
    body: JSON.stringify({ filename, contentType, sizeBytes, projectId }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Voice upload init failed')
  }

  return res.json()
}

/**
 * Create a voice from an uploaded audio/video file.
 */
export async function createVoice(voiceUrl: string, projectId: string): Promise<{ voiceId: string; provider: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/voices/create`, {
    method: 'POST',
    body: JSON.stringify({ voiceUrl, projectId }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Voice creation failed')
  }

  return res.json()
}

/**
 * Pre-upload all frames needed for a generation batch.
 *
 * Takes base64 data URLs and uploads them to R2, returning URLs.
 * This should be called ONCE before starting a batch, then the URLs
 * can be used for all jobs in the batch.
 *
 * @param frames - Object with optional imageUrl, endFrameUrl, imageReferences, elements
 * @returns Object with the same structure but with R2 URLs instead of base64
 * @throws Error with descriptive message if any upload fails
 */
export async function preUploadFrames(frames: {
  imageUrl?: string
  endFrameUrl?: string
  middleFrameUrl?: string
  maskUrl?: string
  imageReferences?: string[]
  elements?: {
    frontalImageUrl: string
    referenceImageUrls?: string[]
    /** Library identity — must survive the upload rewrite (usage links). */
    libraryRef?: { collectionId: string; assetId?: string; versionId?: string }
  }[]
}): Promise<FrameUrls> {
  try {
    const result: FrameUrls = {}
    const isDataUrl = (url: string) => url.startsWith('data:')
    const convertBlobUrlToDataUrl = async (url: string): Promise<string> => {
      if (!url.startsWith('blob:')) {
        return url
      }

      const response = await fetch(url)
      const blob = await response.blob()
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = () => reject(new Error('Failed to read blob as data URL'))
        reader.readAsDataURL(blob)
      })
    }

    const [
      normalizedImageUrl,
      normalizedEndFrameUrl,
      normalizedMiddleFrameUrl,
      normalizedMaskUrl,
      normalizedImageReferences,
      normalizedElements,
    ] = await Promise.all([
      frames.imageUrl ? convertBlobUrlToDataUrl(frames.imageUrl) : Promise.resolve(undefined),
      frames.endFrameUrl ? convertBlobUrlToDataUrl(frames.endFrameUrl) : Promise.resolve(undefined),
      frames.middleFrameUrl ? convertBlobUrlToDataUrl(frames.middleFrameUrl) : Promise.resolve(undefined),
      frames.maskUrl ? convertBlobUrlToDataUrl(frames.maskUrl) : Promise.resolve(undefined),
      frames.imageReferences
        ? Promise.all(frames.imageReferences.map(convertBlobUrlToDataUrl))
        : Promise.resolve(undefined),
      frames.elements
        ? Promise.all(
            frames.elements.map(async element => ({
              ...element,
              frontalImageUrl: await convertBlobUrlToDataUrl(element.frontalImageUrl),
              referenceImageUrls: element.referenceImageUrls
                ? await Promise.all(element.referenceImageUrls.map(convertBlobUrlToDataUrl))
                : undefined,
            })),
          )
        : Promise.resolve(undefined),
    ])

    // Upload start and end frames in parallel
    const framePromises: Promise<void>[] = []

    if (normalizedImageUrl && isDataUrl(normalizedImageUrl)) {
      framePromises.push(
        uploadFrame(normalizedImageUrl).then(r => {
          result.imageUrl = r.url
        }),
      )
    } else if (normalizedImageUrl) {
      result.imageUrl = normalizedImageUrl
    }

    if (normalizedEndFrameUrl && isDataUrl(normalizedEndFrameUrl)) {
      framePromises.push(
        uploadFrame(normalizedEndFrameUrl).then(r => {
          result.endFrameUrl = r.url
        }),
      )
    } else if (normalizedEndFrameUrl) {
      result.endFrameUrl = normalizedEndFrameUrl
    }

    if (normalizedMiddleFrameUrl && isDataUrl(normalizedMiddleFrameUrl)) {
      framePromises.push(
        uploadFrame(normalizedMiddleFrameUrl).then(r => {
          result.middleFrameUrl = r.url
        }),
      )
    } else if (normalizedMiddleFrameUrl) {
      result.middleFrameUrl = normalizedMiddleFrameUrl
    }

    // Inpainting mask: upload any data: URL to R2 so a failed background upload
    // can't leak a base64 mask into generationParams / the /generate payload.
    if (normalizedMaskUrl && isDataUrl(normalizedMaskUrl)) {
      framePromises.push(
        uploadFrame(normalizedMaskUrl).then(r => {
          result.maskUrl = r.url
        }),
      )
    } else if (normalizedMaskUrl) {
      result.maskUrl = normalizedMaskUrl
    }

    // Wait for main frames
    if (framePromises.length > 0) {
      await Promise.all(framePromises)
    }

    // Upload image references in parallel
    if (normalizedImageReferences && normalizedImageReferences.length > 0) {
      const refPromises = normalizedImageReferences.map(ref =>
        isDataUrl(ref) ? uploadFrame(ref).then(r => r.url) : Promise.resolve(ref),
      )
      result.imageReferences = await Promise.all(refPromises)
    }

    // Upload elements in parallel
    if (normalizedElements && normalizedElements.length > 0) {
      const elementPromises = normalizedElements.map(async element => {
        const [frontalUrl, refUrls] = await Promise.all([
          isDataUrl(element.frontalImageUrl)
            ? uploadFrame(element.frontalImageUrl).then(r => r.url)
            : Promise.resolve(element.frontalImageUrl),
          element.referenceImageUrls && element.referenceImageUrls.length > 0
            ? Promise.all(
                element.referenceImageUrls.map(ref =>
                  isDataUrl(ref) ? uploadFrame(ref).then(r => r.url) : Promise.resolve(ref),
                ),
              )
            : Promise.resolve(undefined),
        ])
        return { ...element, frontalImageUrl: frontalUrl, referenceImageUrls: refUrls }
      })
      result.elements = await Promise.all(elementPromises)
    }

    return result
  } catch (error) {
    // Wrap with more context for debugging
    const message = error instanceof Error ? error.message : 'Unknown error'
    throw new Error(`Failed to pre-upload frames: ${message}`)
  }
}

/**
 * Start a new generation job
 */
export async function startGeneration(params: GenerationParams): Promise<{
  jobId: string
  modelId: string
  status: string
  createdAt: string
  frameUrls?: FrameUrls
  assetNumber?: number
  isFirstGeneration?: boolean
}> {
  // Keep a central fallback for direct callers (such as canvas operations),
  // including contextual models that are intentionally absent from the public
  // catalog. If project pricing is unavailable or intentionally private, omit
  // the guard and let the backend's legacy-compatible path price authoritatively.
  let generationParams = params
  if (params.expectedPricing === undefined) {
    const { models, projectPricing } = await fetchModels(undefined, { projectId: params.projectId })
    const publicModel = models.find(model => model.id === params.modelId)
    // A reference request needs a reference term only when the card bills
    // references; a contextual model absent from the catalog keeps the
    // conservative reading.
    const match = {
      hasVideoReferences:
        (params.videoReferences?.length ?? 0) > 0 &&
        (publicModel ? cardBillsVideoReferences(publicModel.pricing) : true),
    }
    const resolvedPricing = resolveProjectModelPricing(projectPricing, params.modelId, params.resolution, match)
    const expectedPricing =
      resolvedPricing?.expectedPricing ??
      (publicModel ? resolveCustomerPricingGuard(publicModel, params.resolution, match) : undefined)
    if (expectedPricing !== undefined) {
      generationParams = { ...params, expectedPricing }
    }
  }
  const res = await fetchWithAuth(`${apiUrl()}/generate`, {
    method: 'POST',
    body: JSON.stringify(generationParams),
  })

  if (!res.ok) {
    const error = await res.json()

    // Handle billing errors specially (insufficient credits + spend cap exceeded)
    if (res.status === 402 && (error.code === 'INSUFFICIENT_CREDITS' || error.code === 'PROJECT_SPEND_CAP_EXCEEDED')) {
      const err = new Error(error.message || 'Billing error') as Error & {
        status: number
        billingInfo: { code: string; [key: string]: unknown }
      }
      err.status = res.status
      err.billingInfo = {
        code: error.code,
        required: error.required,
        available: error.available,
        billing_mode: error.billing_mode,
        message: error.message,
        spendCap: error.spendCap,
        projectSpend: error.projectSpend,
      }
      throw err
    }

    // On a coded 4xx rejection, `error` is the short label ('Monthly member cap
    // reached') and `message` carries the detail the user needs ('You have
    // reached your monthly spend cap of 500 olives.') — prefer the detail.
    // 4xx only: a 5xx `message` is diagnostics, not copy (CREDIT_DEDUCTION_FAILED
    // puts the raw Autumn/provider error string there), and callers such as the
    // Draft Enhance dialog render this verbatim.
    const codedMessage =
      res.status < 500 && typeof error.code === 'string' && typeof error.message === 'string' ? error.message : null
    const message =
      codedMessage ||
      (error.error === 'Failed to start generation' && error.message
        ? error.message
        : error.error || error.message || 'Generation failed')
    const err = new Error(message) as Error & {
      status?: number
      code?: string
      canUpgrade?: boolean
      refundedAmount?: number
      refundAttempted?: boolean
      refundSucceeded?: boolean
    }
    // Carry the HTTP status: callers classify retryability on it rather than
    // sniffing digits out of the message (which silently misreads any rejection
    // whose body puts a machine code where the human sentence should be).
    err.status = res.status
    err.code = error.code
    // Server's verdict on whether a plan change would clear this refusal — it
    // knows which org is billed, which the client cannot infer from the code.
    if (typeof error.canUpgrade === 'boolean') err.canUpgrade = error.canUpgrade
    if (typeof error.refundedAmount === 'number') err.refundedAmount = error.refundedAmount
    if (typeof error.refundAttempted === 'boolean') err.refundAttempted = error.refundAttempted
    if (typeof error.refundSucceeded === 'boolean') err.refundSucceeded = error.refundSucceeded
    throw err
  }

  return res.json()
}

/**
 * Fetch the full Asset record for a generated artifact by its output asset ID.
 *
 * Used by the headless artifact inspector (generated outputs that
 * were never promoted into the project's Yjs `assets` map). Returns `null` on
 * 404 so callers can decide between fallback UI and an explicit error.
 */
export async function getAssetRecord(
  assetId: string,
): Promise<{ asset: import('@/types').Asset; jobId: string; projectId: string } | null> {
  const res = await fetchWithAuth(`${apiUrl()}/assets/${assetId}/record`)

  if (res.status === 404) {
    return null
  }

  if (!res.ok) {
    throw new Error(`Failed to fetch asset record: ${res.statusText}`)
  }

  return res.json()
}

/**
 * List jobs for a project
 */
export async function listProjectJobs(projectId: string): Promise<Job[]> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/jobs`)

  if (!res.ok) {
    throw new Error(`Failed to fetch jobs: ${res.statusText}`)
  }

  const data = await res.json()
  return data.jobs
}

/**
 * Fetch the asset → user mapping for all generations in a project.
 * Used to backfill userId on existing Yjs assets.
 */
export async function getGenerationAuthors(projectId: string): Promise<Record<string, string>> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/generation-authors`)

  if (!res.ok) {
    throw new Error(`Failed to fetch generation authors: ${res.statusText}`)
  }

  const data = await res.json()
  return data.authors ?? {}
}

export interface AssetPromptMatch {
  assetId: string
  promptPreview: string
  thumbnailUri?: string
}

/**
 * Fuzzy substring search across completed-generation prompts in a project.
 * Tokens are AND'd server-side; tokens shorter than 3 chars are ignored.
 * Pass an `AbortSignal` so stale responses don't overwrite newer ones.
 */
export async function searchAssetsByPrompt(
  projectId: string,
  query: string,
  options: { signal?: AbortSignal; limit?: number } = {},
): Promise<AssetPromptMatch[]> {
  const params = new URLSearchParams({ q: query })
  if (options.limit !== undefined) params.set('limit', String(options.limit))
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/search-assets?${params.toString()}`, {
    signal: options.signal,
  })

  if (!res.ok) {
    throw new Error(`Failed to search assets: ${res.statusText}`)
  }

  const data = (await res.json()) as { matches?: AssetPromptMatch[] }
  return data.matches ?? []
}

/**
 * Snapshot of AI agents (Claude, etc.) actively using the MCP on this project.
 * Called on canvas mount to seed the collaborator avatar strip before the
 * first `mcp_agent_presence` WebSocket broadcast lands.
 */
export async function getMcpAgents(projectId: string): Promise<McpAgentPresence[]> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/mcp-agents`)

  if (!res.ok) {
    throw new Error(`Failed to fetch MCP agents: ${res.statusText}`)
  }

  const data = (await res.json()) as { agents?: McpAgentPresence[] }
  return data.agents ?? []
}

/**
 * Batch fetch multiple jobs by IDs.
 * Used for status recovery on WebSocket reconnect.
 */
export async function getJobsBatch(jobIds: string[]): Promise<Job[]> {
  if (jobIds.length === 0) {
    return []
  }

  const res = await fetchWithAuth(`${apiUrl()}/jobs/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jobIds }),
  })

  if (!res.ok) {
    throw new Error(`Failed to fetch jobs: ${res.statusText}`)
  }

  const data = await res.json()
  return data.jobs
}

/**
 * Get public URL for an asset file.
 *
 * @deprecated This function is no longer used for downloads. Use `downloadAsset()` from
 * `@/lib/download` instead, which proxies through the backend to avoid CORS issues.
 * Kept for potential future use cases that need direct URL access.
 * See UNADDRESSED.md for cleanup considerations.
 *
 * @param assetId - The asset ID
 * @param filename - The filename as stored on the backend (e.g., "{assetId}.mp4", "{assetId}.jpg")
 * @returns The public URL for downloading the asset
 */
/**
 * `?projectId=` for the asset routes: the open project is the claimed context
 * that lets a legacy owner-less asset resolve (see assetProjectContext.ts).
 * Empty outside a project.
 */
function assetProjectQuery(prefix: '?' | '&'): string {
  const projectId = getAssetProjectContext()
  return projectId ? `${prefix}projectId=${encodeURIComponent(projectId)}` : ''
}

export async function getAssetUrl(assetId: string, filename: string): Promise<string> {
  const res = await fetchWithAuth(
    `${apiUrl()}/assets/${assetId}/url?filename=${encodeURIComponent(filename)}${assetProjectQuery('&')}`,
  )

  if (!res.ok) {
    throw new Error(`Failed to get asset URL: ${res.statusText}`)
  }

  const data = await res.json()
  return data.downloadUrl
}

/**
 * Get storage URL for a file.
 *
 * Handles two formats for gradual migration (see UNADDRESSED.md for cleanup plan):
 * 1. Full URLs (legacy): "https://pub-xyz.r2.dev/v0.2/assets/abc/proxy.mp4"
 *    - Returns unchanged (backward compatibility for old assets)
 * 2. URIs with prefix (new): "v0.2/assets/abc/proxy.mp4"
 *    - Appends to NEXT_PUBLIC_STORAGE_URL
 *    - Local dev: http://localhost:3001/storage/v0.2/assets/... → backend redirect
 *    - Production: https://pub-xyz.r2.dev/v0.2/assets/... → direct R2 access (no redirect!)
 *
 * The new URI format includes the version prefix (v0.2), making assets resilient to
 * storage prefix changes. When backend moves to v0.3, old assets still work with v0.2 URIs.
 */
export function getStorageUrl(uriOrUrl: string): string {
  // Case 1: Already a full URL or inline data — apply legacy host rewriter for stored
  // martini.video URLs, otherwise return unchanged
  if (
    uriOrUrl.startsWith('http://') ||
    uriOrUrl.startsWith('https://') ||
    uriOrUrl.startsWith('data:') ||
    uriOrUrl.startsWith('blob:')
  ) {
    return rewriteLegacyStorageHost(uriOrUrl, 'getStorageUrl')
  }

  // Case 2: URI with prefix (new format) - append to storage base URL
  const storageBaseUrl = process.env.NEXT_PUBLIC_STORAGE_URL
  if (!storageBaseUrl) {
    throw new Error('NEXT_PUBLIC_STORAGE_URL is not configured')
  }

  // Normalize slashes to prevent double slashes in URLs
  // Remove trailing slash from base, leading slash from URI
  const trimmedBase = storageBaseUrl.endsWith('/') ? storageBaseUrl.slice(0, -1) : storageBaseUrl
  const trimmedUri = uriOrUrl.startsWith('/') ? uriOrUrl.slice(1) : uriOrUrl

  return `${trimmedBase}/${trimmedUri}`
}

/**
 * Get the asset specification (generation parameters) for an asset.
 * Used by the "Reuse Settings" feature.
 * Results are cached since specs are immutable after generation.
 *
 * Uploaded assets store a different `spec.json` shape (`UploadSpec` in
 * backend/src/upload.ts — filename/mimeType/dimensions, no `model` and no
 * `prompt`), and the backend serves it verbatim. Those carry no generation
 * parameters, so we normalize them to `null` — the same "no spec" outcome
 * callers already handle — instead of letting a `ShotSpec`-shaped cast reach
 * consumers that dereference `spec.model`.
 */
export async function getAssetSpec(assetId: string): Promise<ShotSpec | null> {
  // Check cache first
  if (assetSpecCache.has(assetId)) {
    return assetSpecCache.get(assetId)!
  }

  const res = await fetchWithAuth(`${apiUrl()}/assets/${assetId}/spec${assetProjectQuery('?')}`)

  if (res.status === 404) {
    // Don't cache 404s - allow retry on next request
    // (spec.json might not be ready yet during generation race conditions)
    return null
  }

  if (!res.ok) {
    throw new Error(`Failed to get asset spec: ${res.statusText}`)
  }

  const spec = (await res.json()) as ShotSpec | null
  const generationSpec = spec?.model ? spec : null
  assetSpecCache.set(assetId, generationSpec)
  return generationSpec
}

export type VariableUsageSpec = {
  assetId: string
  prompt?: string
  userPrompt?: string
  multiPrompt?: Array<{ prompt: string; userPrompt?: string; duration: number }>
  variableSnapshot?: Record<string, { name: string; value: string; color?: string }>
}

/**
 * Batch lookup of the slim spec fields needed by the Variable Usage panel.
 * Returns only assets whose `variableSnapshot` contains `variableId`.
 */
export async function getVariableUsageSpecs(assetIds: string[], variableId: string): Promise<VariableUsageSpec[]> {
  if (assetIds.length === 0) return []
  const res = await fetchWithAuth(`${apiUrl()}/assets/specs/variable-usage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assetIds, variableId }),
  })
  if (!res.ok) {
    throw new Error(`Failed to look up variable usage specs: ${res.statusText}`)
  }
  const data = (await res.json()) as { usages: VariableUsageSpec[] }
  return data.usages
}

/**
 * Get the job metadata for an asset.
 * Results are cached since job metadata is immutable after completion.
 */
/**
 * Replace a video's audio track with an audio asset from the same project.
 * Creates a NEW video node (child of the source video); the source is untouched.
 */
export async function replaceVideoAudio(
  videoAssetId: string,
  audioAssetId: string,
  projectId: string,
  requestId: string,
): Promise<{ asset: Asset }> {
  const res = await fetchWithAuth(`${apiUrl()}/assets/${videoAssetId}/replace-audio`, {
    method: 'POST',
    body: JSON.stringify({ projectId, audioAssetId, requestId }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new Error(
      typeof body?.error === 'string' ? body.error : 'Could not replace the audio track. Please try again.',
    )
  }

  return res.json()
}

export async function getAssetJob(assetId: string): Promise<JobMetadata | null> {
  // Check cache first
  if (assetJobCache.has(assetId)) {
    return assetJobCache.get(assetId)!
  }

  const res = await fetchWithAuth(`${apiUrl()}/assets/${assetId}/job${assetProjectQuery('?')}`)

  if (res.status === 404) {
    // Don't cache 404s - allow retry on next request
    return null
  }

  if (!res.ok) {
    throw new Error(`Failed to get asset job: ${res.statusText}`)
  }

  const job = await res.json()
  assetJobCache.set(assetId, job)
  return job
}

/**
 * Delete an asset from the project.
 * R2 files are retained; only the Yjs reference is removed.
 * Optionally records deletion metadata for auditing.
 */
export async function deleteAsset(
  assetId: string,
  metadata?: {
    projectId?: string
    shotNumber?: number
    assetType?: string
    operation?: string
  },
): Promise<{ deleted: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/assets/${assetId}`, {
    method: 'DELETE',
    body: metadata ? JSON.stringify(metadata) : undefined,
  })

  if (!res.ok) {
    throw new Error(`Failed to delete asset: ${res.statusText}`)
  }

  return res.json()
}

/**
 * Delete a set by its asset ID (soft delete).
 * Called from sidebar delete flow. No longer called from canvas delete — canvas
 * deletion goes through removeNodes (undoable) and leaves the DB record as an orphan.
 * projectId is required to scope deletion to the correct project (avoids cross-project deletes on duplicated projects).
 */
export async function deleteSetByAssetId(assetId: string, projectId: string): Promise<{ deleted: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/sets/by-asset/${assetId}`, {
    method: 'DELETE',
    body: JSON.stringify({ projectId }),
  })

  if (!res.ok) {
    throw new Error(`Failed to delete set: ${res.statusText}`)
  }

  return res.json()
}

// ============================================================================
// Video Upload API
// ============================================================================

/**
 * Upload initialization result from backend.
 */
export interface UploadInitResult {
  assetId: string
  uploadUrl: string | null
  directUpload: boolean
  expiresIn: number
  projectId: string
  filename: string
  assetNumber?: number
}

/**
 * Temporary video upload init result for set creation inputs.
 */
export interface SetVideoUploadInitResult {
  assetId: string
  uploadUrl: string | null
  directUpload: boolean
  expiresIn: number
  url: string | null
  contentType: string
}

/**
 * Video metadata extracted on the client side.
 */
export interface VideoMetadata {
  filename: string
  mimeType: string
  duration: number // in seconds
  width: number
  height: number
  /** File size in bytes — binds the presigned PUT's Content-Length. */
  sizeBytes: number
}

/**
 * Audio metadata extracted on the client side.
 */
export interface AudioMetadata {
  filename: string
  mimeType: string
  duration: number // in seconds
  assetType: 'audio'
  /** File size in bytes — binds the presigned PUT's Content-Length. */
  sizeBytes: number
}

/**
 * Image metadata extracted on the client side.
 */
export interface ImageMetadata {
  filename: string
  mimeType: string
  duration: number // Duration for timeline (frontend-set, default 5s)
  width: number
  height: number
  assetType: 'image'
  /** File size in bytes — binds the presigned PUT's Content-Length. */
  sizeBytes: number
}

/**
 * Initialize a media upload (video, audio, or image).
 * Returns a presigned URL for direct upload to R2, or null for local dev.
 */
export async function initUpload(
  projectId: string,
  metadata: VideoMetadata | AudioMetadata | ImageMetadata,
  assetNumberHint?: number,
): Promise<UploadInitResult> {
  const res = await fetchWithAuth(`${apiUrl()}/upload/init`, {
    method: 'POST',
    body: JSON.stringify({
      projectId,
      ...metadata,
      assetNumberHint,
    }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to initialize upload')
  }

  return res.json()
}

/**
 * Initialize a direct video upload for set creation inputs.
 */
export async function initSetVideoUpload(
  filename: string,
  contentType: string,
  sizeBytes: number,
): Promise<SetVideoUploadInitResult> {
  const res = await fetchWithAuth(`${apiUrl()}/upload/video/init`, {
    method: 'POST',
    body: JSON.stringify({
      filename,
      contentType,
      sizeBytes,
    }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to initialize video upload')
  }

  return res.json()
}

/**
 * Upload a file directly to the backend (for local dev when R2 is not available).
 */
export async function uploadFileToBackend(assetId: string, projectId: string, file: File): Promise<void> {
  const token = await getAccessToken()
  if (!token) {
    throw new Error('Authentication required')
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('projectId', projectId)

  const res = await fetch(`${apiUrl()}/upload/${assetId}/file`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      // Don't set Content-Type - browser will set it with boundary for FormData
    },
    body: formData,
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to upload file')
  }
}

/**
 * Complete a media upload (video, audio, or image) and trigger processing.
 */
export async function completeUpload(
  assetId: string,
  projectId: string,
  metadata: VideoMetadata | AudioMetadata | ImageMetadata,
): Promise<{ assetId: string; status: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/upload/complete`, {
    method: 'POST',
    body: JSON.stringify({
      assetId,
      projectId,
      ...metadata,
    }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to complete upload')
  }

  return res.json()
}

/** No upload progress for this long means the transfer is stuck; abort and retry it. */
const R2_UPLOAD_STALL_MS = 60_000
/** Total PUT attempts before giving up (first try + retries). */
const R2_UPLOAD_MAX_ATTEMPTS = 3
const R2_UPLOAD_RETRY_DELAYS_MS = [1_000, 4_000]

function isRetryableUploadFailure(err: UploadTransferError): boolean {
  if (err.reason === 'network' || err.reason === 'stalled') return true
  return err.reason === 'http' && (err.status === 408 || err.status === 429 || err.status >= 500)
}

function putFileToR2Once(
  uploadUrl: string,
  file: File,
  attempt: number,
  onProgress: ((progress: number) => void) | undefined,
  contentType: string | undefined,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let stalled = false
    let stallTimer: ReturnType<typeof setTimeout> | undefined
    const armStallTimer = () => {
      clearTimeout(stallTimer)
      stallTimer = setTimeout(() => {
        stalled = true
        xhr.abort()
      }, R2_UPLOAD_STALL_MS)
    }

    xhr.upload.addEventListener('progress', event => {
      armStallTimer()
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })

    xhr.addEventListener('loadend', () => clearTimeout(stallTimer))

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else if (xhr.status === 412 && attempt > 1) {
        // The create-only PUT found the object already there: an earlier attempt
        // landed and only its response was lost. The file is in R2.
        resolve()
      } else {
        reject(new UploadTransferError('http', xhr.status, attempt))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new UploadTransferError('network', 0, attempt))
    })

    xhr.addEventListener('abort', () => {
      reject(new UploadTransferError(stalled ? 'stalled' : 'aborted', 0, attempt))
    })

    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', contentType || file.type || 'application/octet-stream')
    // If-None-Match is signed into the presigned URL (create-only PUT — cannot
    // overwrite an existing object); the request fails signature validation
    // without it.
    xhr.setRequestHeader('If-None-Match', '*')
    armStallTimer()
    xhr.send(file)
  })
}

/**
 * Upload a file directly to R2 using a presigned URL.
 * Returns a promise that resolves when upload is complete.
 *
 * A dropped connection or a transfer that stops progressing for a minute is
 * retried (R2_UPLOAD_MAX_ATTEMPTS in total) — a large batch on a flaky link
 * used to leave tiles stuck at "Uploading" until reconciliation failed them.
 *
 * @param uploadUrl - Presigned URL from initUpload
 * @param file - File to upload
 * @param onProgress - Optional progress callback (0-100)
 * @param contentType - Optional MIME type to match the presigned upload signature
 * @throws UploadTransferError when every attempt failed
 */
export async function uploadToR2(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
  contentType?: string,
): Promise<void> {
  for (let attempt = 1; ; attempt++) {
    try {
      await putFileToR2Once(uploadUrl, file, attempt, onProgress, contentType)
      return
    } catch (err) {
      if (!(err instanceof UploadTransferError)) throw err
      if (attempt >= R2_UPLOAD_MAX_ATTEMPTS || !isRetryableUploadFailure(err)) throw err
      console.warn(`[uploadToR2] attempt ${attempt} failed (${err.reason} ${err.status}), retrying`)
      onProgress?.(0)
      await new Promise(resolve => setTimeout(resolve, R2_UPLOAD_RETRY_DELAYS_MS[attempt - 1]))
    }
  }
}

// =============================================================================
// Billing API
// =============================================================================

export interface MembershipsInfo {
  personalOrg: {
    id: string
    name: string
  } | null
  teamOrgs: {
    id: string
    name: string
    slug: string | null
    role: OrganizationRole
  }[]
}

export interface BillingInfo {
  billingEnabled: boolean
  personalOrg: {
    id: string
    balance: number | null
    hasFalKey?: boolean
    subscriptionPlan: string | null
    subscriptionStatus: string | null
    currentPeriodEnd: string | null
    cancelAtPeriodEnd?: boolean
    enterprisePasswordPolicyEnabled: boolean
    enterpriseSessionTimeoutEnabled: boolean
  } | null
  teamOrgs: {
    id: string
    name: string
    slug: string | null
    balance: number | null
    hasFalKey?: boolean
    role: string
    billingEmail: string | null
    subscriptionPlan: string | null
    subscriptionStatus: string | null
    currentPeriodEnd: string | null
    cancelAtPeriodEnd?: boolean
    enterprisePasswordPolicyEnabled: boolean
    enterpriseSessionTimeoutEnabled: boolean
  }[]
  pendingInvitationsCount?: number
  effectivePlan?: string
  isTeamProvided?: boolean
  effectivePlanProviders?: { teamId: string; teamName: string; teamSlug: string | null; teamPlan: string }[]
  enterpriseSecurity?: {
    passwordPolicyEnabled: boolean
    sessionInactivityTimeoutEnabled: boolean
    sessionInactivityTimeoutMs: number
  }
  hasRedundantPersonalSub?: boolean
  /** Whether a new personal subscription would receive the once-ever +100 first-subscription bonus */
  firstSubBonusEligible?: boolean
  /** Whether this user's next personal subscription checkout gets the automatic 50%-off-first-month coupon */
  firstMonthPromoEligible?: boolean
  /** Whether the 50%-off-first-month promo is live at all (new-team checkouts always qualify) */
  firstMonthPromoActive?: boolean
  /** Whether plan-tier top-up bonuses are live (flag-gated; rates in shared.ts) */
  topupBonusEnabled?: boolean
}

export interface BillingPricingInfo {
  billingEnabled: boolean
  personalOrg: {
    subscriptionPlan: string | null
    subscriptionStatus: string | null
  } | null
  hasTeamOrg: boolean
  effectivePlan: string
  /** Whether this user's next personal subscription checkout gets the automatic 50%-off-first-month coupon */
  firstMonthPromoEligible?: boolean
  /** Whether the 50%-off-first-month promo is live at all (new-team checkouts always qualify) */
  firstMonthPromoActive?: boolean
  /** Whether plan-tier top-up bonuses are live (flag-gated; rates in shared.ts) */
  topupBonusEnabled?: boolean
}

export interface ProjectBillingInfo {
  billingOrgId: string | null
  billingMode: 'personal_tab' | 'team_tab' | 'generator_pays'
  balance: number | null
  billingEnabled: boolean
  spendCap: number | null
  spendAlertThreshold: number | null
  projectSpend: number
  spendCapReached: boolean
  spendCapRemaining: number | null
}

export interface InsufficientCreditsError {
  code: 'INSUFFICIENT_CREDITS'
  required: number
  available: number | null
  billing_mode: string
  message: string
}

/**
 * Get fast membership and role information for the current user.
 */
export async function getMemberships(): Promise<MembershipsInfo> {
  const res = await fetchWithAuth(`${apiUrl()}/memberships`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to get memberships')
  }

  return res.json()
}

/**
 * Get billing information for the current user.
 */
export async function getBillingInfo(): Promise<BillingInfo> {
  const res = await fetchWithAuth(`${apiUrl()}/billing`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to get billing info')
  }

  return res.json()
}

/**
 * Get the minimal billing information needed by the pricing page.
 */
export async function getBillingPricingInfo(): Promise<BillingPricingInfo> {
  const res = await fetchWithAuth(`${apiUrl()}/billing/pricing`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to get pricing billing info')
  }

  return res.json()
}

/**
 * Get billing information for a project.
 */
export async function getProjectBilling(projectId: string): Promise<ProjectBillingInfo> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/billing`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to get project billing info')
  }

  return res.json()
}

export const BYOK_PROVIDER_IDS = ['fal.ai', 'enhancor', 'elevenlabs', 'typecast'] as const
export type ByokProviderId = (typeof BYOK_PROVIDER_IDS)[number]

export interface ByokProviderAccess {
  /** The org holds this provider's grant — a Backstage → BYOK switch (backend byokProviderAccess). */
  granted: boolean
  hasKey: boolean
  billingMode: 'martini' | 'byok'
  /** Whether the provider's key section may render at all. */
  visible: boolean
}

export type ByokProvidersAccess = Record<ByokProviderId, ByokProviderAccess>

/** Which BYOK providers an org may see. Mirrors backend byokProviderAccess. */
export async function getByokProviders(orgId: string): Promise<{ providers: ByokProvidersAccess }> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-providers`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'BYOK_PROVIDERS_FETCH_FAILED',
      error.error ?? 'Could not load provider access. Try refreshing.',
    )
  }
  return res.json()
}

export interface ProviderByokStatus {
  billingMode: 'martini' | 'byok'
  hasKey: boolean
  isByok: boolean
  modelAccess?: boolean
  misconfigured?: { reason: 'missing_secret' }
  byokPricing?: Record<string, number>
}

export interface ByokStatus {
  provider?: 'fal.ai'
  billingMode: 'martini' | 'byok'
  hasFalKey: boolean
  isByok: boolean
  misconfigured?: { reason: 'missing_secret' }
  byokPricing?: Record<string, number>
  providers?: Partial<Record<ByokProviderId, ProviderByokStatus>>
}

/**
 * Returns whether an organization has a BYOK FAL key, and the per-endpoint
 * USD unit prices that key resolves to (pre-warmed from FAL's pricing API).
 */
export async function getByokStatus(orgId: string): Promise<ByokStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-status`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to get key status')
  }

  return res.json()
}

export interface ByokKeyStatus {
  enabled: boolean
  modelAccess?: boolean
  hasKey: boolean
  hasAdminKey?: boolean
  billingMode: 'martini' | 'byok'
  isByok: boolean
  canUseByok: boolean
  misconfigured?: { reason: 'missing_secret' }
  keyPrefix?: string
  lastFour?: string
  updatedAt?: string
  adminKeyPrefix?: string
  adminLastFour?: string
  adminUpdatedAt?: string
}

export interface ByokKeySaveResult {
  hasKey: true
  billingMode: 'byok'
  isByok: true
  canUseByok: true
  keyPrefix: string
  lastFour: string
  updatedAt: string
}

export class ByokKeyError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'ByokKeyError'
    this.code = code
  }
}

export async function getByokKey(orgId: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-key`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'BYOK_KEY_FETCH_FAILED',
      error.error ?? 'Could not load key status. Try refreshing.',
    )
  }
  return res.json()
}

export async function saveByokKey(orgId: string, key: string): Promise<ByokKeySaveResult> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-key`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(error.code ?? 'BYOK_KEY_SAVE_FAILED', error.error ?? 'Could not save key. Try again.')
  }
  return res.json()
}

export async function saveByokAdminKey(orgId: string, key: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-admin-key`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(error.code ?? 'BYOK_ADMIN_KEY_SAVE_FAILED', error.error ?? 'Failed to save FAL Admin key')
  }
  return res.json()
}

export async function setByokBillingMode(orgId: string, billingMode: 'martini' | 'byok'): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-key`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ billingMode }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'BYOK_BILLING_MODE_UPDATE_FAILED',
      error.error ?? 'Could not update billing mode. Try again.',
    )
  }
  return res.json()
}

export async function deleteByokAdminKey(orgId: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-admin-key`, { method: 'DELETE' })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'BYOK_ADMIN_KEY_DELETE_FAILED',
      error.error ?? 'Failed to delete FAL Admin key',
    )
  }
  return res.json()
}

export async function deleteByokKey(orgId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-key`, { method: 'DELETE' })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(error.code ?? 'BYOK_KEY_DELETE_FAILED', error.error ?? 'Could not remove key. Try again.')
  }
}

export async function getEnhancorByokKey(orgId: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-enhancor-key`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'ENHANCOR_BYOK_KEY_FETCH_FAILED',
      error.error ?? 'Could not load Enhancor key status. Try refreshing.',
    )
  }
  return res.json()
}

export async function saveEnhancorByokKey(orgId: string, key: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-enhancor-key`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'ENHANCOR_BYOK_KEY_SAVE_FAILED',
      error.error ?? 'Could not save Enhancor key. Try again.',
    )
  }
  return res.json()
}

export async function deleteEnhancorByokKey(orgId: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-enhancor-key`, { method: 'DELETE' })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'ENHANCOR_BYOK_KEY_DELETE_FAILED',
      error.error ?? 'Could not remove Enhancor key. Try again.',
    )
  }
  return res.json()
}

export async function getElevenLabsByokKey(orgId: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-elevenlabs-key`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'ELEVENLABS_BYOK_KEY_FETCH_FAILED',
      error.error ?? 'Could not load ElevenLabs key status. Try refreshing.',
    )
  }
  return res.json()
}

export async function saveElevenLabsByokKey(orgId: string, key: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-elevenlabs-key`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'ELEVENLABS_BYOK_KEY_SAVE_FAILED',
      error.error ?? 'Could not save ElevenLabs key. Try again.',
    )
  }
  return res.json()
}

export async function deleteElevenLabsByokKey(orgId: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-elevenlabs-key`, { method: 'DELETE' })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'ELEVENLABS_BYOK_KEY_DELETE_FAILED',
      error.error ?? 'Could not remove ElevenLabs key. Try again.',
    )
  }
  return res.json()
}

export async function getTypecastByokKey(orgId: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-typecast-key`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'TYPECAST_BYOK_KEY_FETCH_FAILED',
      error.error ?? 'Could not load Typecast key status. Try refreshing.',
    )
  }
  return res.json()
}

export async function saveTypecastByokKey(orgId: string, key: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-typecast-key`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'TYPECAST_BYOK_KEY_SAVE_FAILED',
      error.error ?? 'Could not save Typecast key. Try again.',
    )
  }
  return res.json()
}

export async function deleteTypecastByokKey(orgId: string): Promise<ByokKeyStatus> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-typecast-key`, { method: 'DELETE' })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'TYPECAST_BYOK_KEY_DELETE_FAILED',
      error.error ?? 'Could not remove Typecast key. Try again.',
    )
  }
  return res.json()
}

export interface ElevenLabsVoice {
  voiceId: string
  name: string
  category?: string
  previewUrl?: string
}

export async function getElevenLabsVoices(orgId: string): Promise<{ voices: ElevenLabsVoice[] }> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/elevenlabs/voices`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ByokKeyError(
      error.code ?? 'ELEVENLABS_VOICES_FETCH_FAILED',
      error.error ?? 'Could not load ElevenLabs voices. Try again.',
    )
  }
  return res.json()
}

export type FalUsageUnavailableReason =
  | 'missing_generation_key'
  | 'missing_admin_key'
  | 'not_admin_key'
  | 'key_not_in_admin_workspace'
  | 'admin_key_rejected'
  | 'usage_fetch_failed'

export interface ByokFalUsageTotal {
  available: boolean
  reason?: FalUsageUnavailableReason
  totalUsd: number | null
  currency: 'USD'
  from: string
  to: string
  authMethod?: string
  matchedLineCount?: number
}

export async function getByokFalUsageTotal(
  orgId: string,
  options?: { from?: string; to?: string },
): Promise<ByokFalUsageTotal> {
  const params = new URLSearchParams()
  if (options?.from) params.set('from', options.from)
  if (options?.to) params.set('to', options.to)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/byok-fal-usage${qs}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to fetch FAL usage')
  }
  return res.json()
}

export interface SpendSummary {
  period: { from: string; to: string }
  martini: { olivesNet: number; generationCount: number }
  byok: {
    currency: 'USD'
    usdNet: number | null
    generationCount: number
    hasFalKey: boolean
    costSourceBreakdown: { estimated: number; reconciled: number; null: number }
    spendSource?: 'none' | 'unavailable' | 'fal_usage' | 'estimated'
    falUsage?: ByokFalUsageTotal
  }
  meta: { truncated: boolean; rowCount: number; transactionCount: number }
}

/** Per-provider state on the Backstage BYOK page. `granted` is the one switch staff control. */
export interface BackstageByokProviderState {
  granted: boolean
  billingMode: 'martini' | 'byok'
  hasKey: boolean
  lastFour: string | null
  updatedAt: string | null
  /** billing_mode = byok with no saved key. */
  misconfigured: boolean
}

export interface BackstageByokOrgRow {
  orgId: string
  name: string
  type: 'personal' | 'team'
  slug: string | null
  ownerEmail: string | null
  planTier: string | null
  providers: Record<ByokProviderId, BackstageByokProviderState>
  /** Most recent key change across providers. */
  updatedAt: string | null
}

export const BACKSTAGE_CAPABILITIES = [
  'handbook:read',
  'i18n:manage',
  'access:manage',
  'users:delete',
  'billing:manage',
  'pipeline:debug',
  'distillery:operate',
  'workflows:operate',
  'projects:transfer',
  'users:manage',
  'projects:recover',
] as const

export type BackstageCapability = (typeof BACKSTAGE_CAPABILITIES)[number]

export type BackstageAccessSource = 'root_owner' | 'grant' | 'local_dev' | 'none'

export interface BackstageAccessInfo {
  email: string
  capabilities: BackstageCapability[]
  source: BackstageAccessSource
  isRootOwner: boolean
  grantId: string | null
  expiresAt: string | null
  deniedReason: string | null
}

export interface BackstageAccessGrant {
  id: string
  email: string
  userId: string | null
  capabilities: BackstageCapability[]
  status: 'active' | 'revoked'
  expiresAt: string | null
  grantedBy: string | null
  grantedReason: string
  createdAt: string
  updatedAt: string
  revokedAt: string | null
  revokedBy: string | null
  revokedReason: string | null
}

export interface BackstageAccessAuditEvent {
  id: string
  eventType: 'grant_created' | 'grant_updated' | 'grant_revoked'
  actorUserId: string | null
  actorEmail: string
  targetGrantId: string | null
  targetEmail: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  reason: string
  requestIp: string | null
  requestUserAgent: string | null
  createdAt: string
}

export interface GhostProjectSearchResult {
  id: string
  name: string
  ownerEmail: string | null
  ownerAlias: string | null
  teamId: string | null
  teamName: string | null
  billingMode: string
  visibility: string
  lastEditedAt: string | null
  memberCount: number
}

/** Search customer projects for ghost mode (backstage, root-owner only). */
export async function searchGhostProjects(q: string): Promise<GhostProjectSearchResult[]> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/projects-admin?q=${encodeURIComponent(q)}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Project search failed')
  }
  return (await res.json()).projects
}

export interface GhostLibrarySearchResult {
  orgId: string
  name: string
  type: 'personal' | 'team'
  ownerEmail: string | null
  ownerAlias: string | null
  billingEmail: string | null
  memberCount: number
  projectCount: number
  createdAt: string | null
}

/** Search customer workspaces for direct library ghost mode (backstage, root-owner only). */
export async function searchGhostLibraries(q: string): Promise<GhostLibrarySearchResult[]> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/projects-admin/libraries?q=${encodeURIComponent(q)}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Library search failed')
  }
  return (await res.json()).libraries
}

export interface EnterGhostResult {
  project: {
    id: string
    name: string
    teamId: string | null
    visibility: string
  }
  targetOrgId: string
  targetOrgName: string
  targetIsTeam: boolean
}

/** Start a ghost session: records the access log, returns the target workspace. */
export async function enterGhostProject(projectId: string, reason: string): Promise<EnterGhostResult> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/projects-admin/${projectId}/ghost`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to enter ghost mode')
  }
  return res.json()
}

/** Validate a stored project ghost session before trusting client-side sessionStorage. */
export async function validateGhostProjectSession(projectId: string): Promise<EnterGhostResult> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/projects-admin/${projectId}/ghost/session`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Ghost session is no longer valid')
  }
  return res.json()
}

export interface EnterLibraryGhostResult {
  targetOrgId: string
  targetOrgName: string
  targetIsTeam: boolean
}

/** Start a direct library ghost session: records the access log, returns the target workspace. */
export async function enterGhostLibrary(orgId: string, reason: string): Promise<EnterLibraryGhostResult> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/projects-admin/libraries/${orgId}/ghost`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to enter library ghost mode')
  }
  return res.json()
}

/** Audit and enable ghost write mode for the active project session. */
export async function enableGhostProjectWriteMode(projectId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/projects-admin/${projectId}/ghost/write-mode`, {
    method: 'POST',
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to enable write mode')
  }
}

/** Audit and enable ghost write mode for a direct library ghost session. */
export async function enableGhostLibraryWriteMode(orgId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/projects-admin/libraries/${orgId}/ghost/write-mode`, {
    method: 'POST',
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to enable library write mode')
  }
}

export async function getBackstageAccess(): Promise<{
  access: BackstageAccessInfo
  capabilities: readonly BackstageCapability[]
}> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/access/me`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to resolve Backstage access')
  }
  return res.json()
}

export async function listBackstageAccessGrants(): Promise<{ grants: BackstageAccessGrant[] }> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/access/grants`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to list Backstage access grants')
  }
  return res.json()
}

export async function listBackstageAccessAuditEvents(options?: {
  limit?: number
}): Promise<{ events: BackstageAccessAuditEvent[] }> {
  const params = new URLSearchParams()
  if (options?.limit) params.set('limit', String(options.limit))
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await fetchWithAuth(`${apiUrl()}/backstage/access/audit${qs}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to list Backstage access audit events')
  }
  return res.json()
}

export async function createBackstageAccessGrant(params: {
  email: string
  capabilities: BackstageCapability[]
  expiresAt?: string | null
  reason: string
}): Promise<{ grant: BackstageAccessGrant }> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/access/grants`, {
    method: 'POST',
    body: JSON.stringify(params),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to create Backstage access grant')
  }
  return res.json()
}

export async function updateBackstageAccessGrant(params: {
  id: string
  capabilities?: BackstageCapability[]
  expiresAt?: string | null
  reason: string
}): Promise<{ grant: BackstageAccessGrant }> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/access/grants/${encodeURIComponent(params.id)}`, {
    method: 'PATCH',
    body: JSON.stringify({
      capabilities: params.capabilities,
      expiresAt: params.expiresAt,
      reason: params.reason,
    }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to update Backstage access grant')
  }
  return res.json()
}

export async function revokeBackstageAccessGrant(params: {
  id: string
  reason: string
}): Promise<{ grant: BackstageAccessGrant }> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/access/grants/${encodeURIComponent(params.id)}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason: params.reason }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to revoke Backstage access grant')
  }
  return res.json()
}

export interface BackstageRevenueDiscountedSubscription {
  email: string | null
  customerId: string
  subscriptionId: string
  planAmount: number
  discountedAmount: number
  discountUsd: number
  percentOff: number | null
  isFullyDiscounted: boolean
  couponDaysLeft: number | null
}

export interface BackstageRevenueSummary {
  asOf: string
  waterfall: {
    totalPlanValue: number
    discountGap: number
    testMrr: number
    scheduledCancellationMrr: number
    realMrr: number
  }
  counts: {
    subscriptions: number
    paying: number
    cancelling: number
    test: number
    discounted: number
    fullyDiscounted: number
  }
  discounts: {
    totalDiscountUsd: number
    fullyDiscountedUsd: number
    partialDiscountUsd: number
    subscriptions: BackstageRevenueDiscountedSubscription[]
  }
  cancellingSoon: Array<{
    email: string | null
    customerId: string
    subscriptionId: string
    monthlyAmount: number
    planAmount: number
    cancelsAt: string | null
  }>
  planBreakdown: Record<string, { count: number; payingCount: number; trialCount: number }>
}

export interface BackstageRevenueWeekMrr {
  realMrr: number
  totalPlanValue: number
  discountGap: number
  testMrr: number
  scheduledCancellationMrr: number
  capturedAt: string
}

export interface BackstageRevenueWeek {
  weekStart: string
  isCurrent: boolean
  subscriptionRevenueUsd: number
  topupRevenueUsd: number
  topupOlives: number
  totalRevenueUsd: number
  mrr: BackstageRevenueWeekMrr | null
}

export interface BackstageRevenueWeeksResponse {
  timeZone: string
  weeks: BackstageRevenueWeek[]
}

export type BackstageRevenueTransactionType = 'all' | 'subscription' | 'topup'

export interface BackstageRevenueTransaction {
  id: string
  occurredAt: string
  email: string | null
  kind: 'subscription' | 'topup' | 'topup_rebuy' | 'topup_auto'
  amountUsd: number
  description: string
  olives: number | null
  stripeUrl: string | null
}

export interface BackstageRevenueTransactionsResponse {
  weekStart: string
  weekEnd: string
  totals: { subscriptionUsd: number; topupUsd: number; count: number }
  truncated: boolean
  transactions: BackstageRevenueTransaction[]
}

export async function getBackstageRevenueSummary(): Promise<BackstageRevenueSummary> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/revenue/summary`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to load revenue summary')
  }
  return res.json()
}

export async function getBackstageRevenueWeeks(options?: { count?: number }): Promise<BackstageRevenueWeeksResponse> {
  const params = new URLSearchParams()
  if (options?.count) params.set('count', String(options.count))
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await fetchWithAuth(`${apiUrl()}/backstage/revenue/weeks${qs}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to load weekly revenue')
  }
  return res.json()
}

export async function getBackstageRevenueTransactions(params: {
  weekStart: string
  type?: BackstageRevenueTransactionType
  search?: string
}): Promise<BackstageRevenueTransactionsResponse> {
  const qp = new URLSearchParams({ weekStart: params.weekStart })
  if (params.type && params.type !== 'all') qp.set('type', params.type)
  if (params.search) qp.set('search', params.search)
  const res = await fetchWithAuth(`${apiUrl()}/backstage/revenue/transactions?${qp}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to load revenue transactions')
  }
  return res.json()
}

export type BackstageEmailSystemId = 'postmark' | 'loops' | 'supabase-auth' | 'dub'

export interface BackstageEmailFlow {
  name: string
  trigger: string
  audience: string
  slug: string | null
  copyHome: string | null
  source: string
  stream: string | null
  tag: string | null
  gate: string | null
  /** Server-evaluated env gate. `null` when the flow has no env gate. */
  gateOpen: boolean | null
  status: 'live' | 'dead'
  /** Sends no mail — an event or property mirror we push to the provider. */
  plumbing: boolean
  configUrl: string | null
}

export interface BackstageEmailSystem {
  id: BackstageEmailSystemId
  name: string
  blurb: string
  configUrl: string | null
  configLabel: string
  flows: BackstageEmailFlow[]
}

export interface BackstageLoopsWorkflowEmail {
  nodeId: string
  emailMessageId: string
  subject: string
  offsetHours: number
  condition: string | null
  copyText: string | null
}

export interface BackstageLoopsWorkflow {
  id: string
  name: string
  status: string
  updatedAt: string
  trigger: { type: string; detail: string | null }
  emails: BackstageLoopsWorkflowEmail[]
  /** Deep link to this workflow's send/open/click numbers in Loops. */
  metricsUrl: string
}

export interface BackstageLoopsTransactional {
  id: string
  name: string
  published: boolean
  wired: string | null
  orphaned: boolean
  subject: string | null
  copyText: string | null
  updatedAt: string
}

export interface BackstageLoopsCampaign {
  id: string
  name: string
  status: string
  sentOrScheduledAt: string | null
  audience: string | null
}

export type BackstageLoopsLiveState =
  | { available: false; reason: 'no_api_key' | 'fetch_failed' }
  | {
      available: true
      workflows: BackstageLoopsWorkflow[]
      transactional: BackstageLoopsTransactional[]
      campaigns: BackstageLoopsCampaign[]
      /** Workflows Loops listed but would not return — their emails are missing. */
      unreadableWorkflows: number
    }

interface BackstageJourneyEntryBase {
  /** Stable identity — two branches can send the same subject at the same offset. */
  id: string
  name: string
  system: BackstageEmailSystemId
  live: boolean
  condition: string | null
  source: string
  slug: string | null
  note: string | null
}

export type BackstageJourneyEntry =
  | (BackstageJourneyEntryBase & { kind: 'clock'; offsetHours: number; clockStart: string })
  | (BackstageJourneyEntryBase & { kind: 'event'; moment: string })

export interface BackstageEmailFlowsState {
  generatedAt: string
  cacheTtlSeconds: number
  systems: BackstageEmailSystem[]
  loops: BackstageLoopsLiveState
  journey: BackstageJourneyEntry[]
}

export async function getEmailFlowsState(options?: { refresh?: boolean }): Promise<BackstageEmailFlowsState> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/email-flows${options?.refresh ? '?refresh=1' : ''}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to load email flows')
  }
  return res.json()
}

export type BackstageProjectTransferBillingMode = 'personal_tab' | 'team_tab' | 'generator_pays'
export type BackstageProjectTransferOrgType = 'personal' | 'team'

export interface BackstageProjectTransferMember {
  userId: string
  email: string | null
  alias: string | null
  role: string
}

export interface BackstageOrgSearchRow {
  orgId: string
  name: string
  slug: string | null
  type: BackstageProjectTransferOrgType
  ownerEmail: string | null
  planTier: string | null
  subscriptionStatus: string | null
  memberCount: number
  adminMembers: BackstageProjectTransferMember[]
  matchingMembers: BackstageProjectTransferMember[]
}

export interface BackstageProjectTransferPreview {
  project: {
    id: string
    name: string
    creatorUserId: string
    creatorEmail: string | null
    teamId: string | null
    teamName: string | null
    billingOrgId: string | null
    billingOrgName: string | null
    billingMode: string
    visibility: 'private' | 'link_view' | 'link_edit'
    updatedAt: string | null
  }
  directProjectMembers: {
    count: number
    retained: boolean
    inertBeforeTransfer: boolean
    inertAfterTransfer: true
    userIds: string[]
  }
  targetOrganization: {
    id: string
    name: string
    slug: string | null
    type: BackstageProjectTransferOrgType
    ownerUserId: string | null
    planTier: string | null
    subscriptionStatus: string | null
    memberCount: number
    memberUserIds: string[]
    adminMembers: BackstageProjectTransferMember[]
  }
  proposed: {
    creatorUserId: string
    teamId: string | null
    billingOrgId: string | null
    billingMode: BackstageProjectTransferBillingMode
    visibility: 'private'
  }
  billingImpact: {
    payer: 'target_team' | 'generating_user'
    summary: string
    detail: string
    nonDefaultReason: string | null
  }
  changes: Array<{
    field: 'created_by' | 'team_id' | 'billing_org_id' | 'billing_mode' | 'visibility'
    before: string | null
    after: string | null
  }>
  warnings: Array<{ code: string; message: string; userIds?: string[]; blocking?: boolean }>
  previewToken: string
}

export async function searchBackstageOrganizations(params: {
  q: string
  type?: 'all' | 'personal' | 'team'
  limit?: number
}): Promise<{ rows: BackstageOrgSearchRow[] }> {
  const search = new URLSearchParams()
  search.set('q', params.q)
  search.set('type', params.type ?? 'team')
  if (params.limit) search.set('limit', String(params.limit))
  const res = await fetchWithAuth(`${apiUrl()}/backstage/orgs/search?${search.toString()}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to search organizations')
  }
  return res.json()
}

export interface BackstageProjectSearchRow {
  id: string
  name: string
  creatorUserId: string
  creatorEmail: string | null
  creatorAlias: string | null
  teamId: string | null
  teamName: string | null
  billingOrgId: string | null
  billingOrgName: string | null
  billingMode: string
  visibility: 'private' | 'link_view' | 'link_edit'
  updatedAt: string | null
  directRole: string | null
  accessReasons: string[]
  group: {
    id: string
    type: BackstageProjectTransferOrgType
    name: string
    ownerEmail: string | null
  }
}

export interface BackstageProjectSearchGroup {
  id: string
  type: BackstageProjectTransferOrgType
  name: string
  ownerEmail: string | null
  projects: BackstageProjectSearchRow[]
}

export async function searchBackstageProjectsByEmail(params: { email: string; limit?: number }): Promise<{
  profile: { userId: string; email: string; alias: string | null }
  groups: BackstageProjectSearchGroup[]
  rows: BackstageProjectSearchRow[]
  meta: { projectCount: number; groupCount: number; limit: number }
}> {
  const search = new URLSearchParams()
  search.set('email', params.email)
  if (params.limit) search.set('limit', String(params.limit))
  const res = await fetchWithAuth(`${apiUrl()}/backstage/projects/search?${search.toString()}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to search projects')
  }
  return res.json()
}

export async function previewBackstageProjectOrgTransfer(params: {
  projectId: string
  targetOrgId: string
  billingMode: BackstageProjectTransferBillingMode
}): Promise<{ preview: BackstageProjectTransferPreview }> {
  const res = await fetchWithAuth(
    `${apiUrl()}/backstage/projects/${encodeURIComponent(params.projectId)}/org-transfer/preview`,
    {
      method: 'POST',
      body: JSON.stringify({
        targetOrgId: params.targetOrgId,
        billingMode: params.billingMode,
      }),
    },
  )
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to preview project transfer')
  }
  return res.json()
}

export async function executeBackstageProjectOrgTransfer(params: {
  projectId: string
  targetOrgId: string
  billingMode: BackstageProjectTransferBillingMode
  reason: string
  previewToken: string
}): Promise<{ success: true; preview: BackstageProjectTransferPreview; performedAt: string }> {
  const res = await fetchWithAuth(
    `${apiUrl()}/backstage/projects/${encodeURIComponent(params.projectId)}/org-transfer`,
    {
      method: 'POST',
      body: JSON.stringify({
        targetOrgId: params.targetOrgId,
        billingMode: params.billingMode,
        reason: params.reason,
        previewToken: params.previewToken,
      }),
    },
  )
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to transfer project')
  }
  return res.json()
}

export async function listBackstageByokOrgs(filters?: {
  q?: string
  hasKey?: boolean
}): Promise<{ rows: BackstageByokOrgRow[] }> {
  const params = new URLSearchParams()
  if (filters?.q) params.set('q', filters.q)
  if (filters?.hasKey !== undefined) params.set('hasKey', String(filters.hasKey))
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await fetchWithAuth(`${apiUrl()}/backstage/byok${qs}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to list BYOK orgs')
  }
  return res.json()
}

/** Grant or revoke one BYOK provider for one org — the single switch behind byokProviderAccess. */
export async function setBackstageByokProviderGranted(
  orgId: string,
  provider: ByokProviderId,
  enabled: boolean,
): Promise<{ orgId: string; provider: ByokProviderId; granted: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/backstage/byok/${orgId}/providers/${encodeURIComponent(provider)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to update BYOK grant')
  }
  return res.json()
}

export async function getOrgSpendSummary(
  orgId: string,
  options?: { from?: string; to?: string },
): Promise<SpendSummary> {
  const params = new URLSearchParams()
  if (options?.from) params.set('from', options.from)
  if (options?.to) params.set('to', options.to)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/spend-summary${qs}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to fetch spend summary')
  }
  return res.json()
}

export async function getOrganizationMcpUsage(orgId: string): Promise<McpUsageResponse | null> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/mcp-usage`)
  if (res.status === 403) return null
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to fetch MCP usage')
  }
  return res.json()
}

export async function getOrganizationMcpUsageAccess(orgId: string): Promise<{ enabled: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/mcp-usage-access`)
  if (res.status === 403 || res.status === 404) return { enabled: false }
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to fetch MCP usage access')
  }
  return res.json()
}

export interface EnterpriseAgreementRate {
  resolution: '480p' | '720p'
  /** Copy describing the negotiated rule for standard output, or null when standard pricing applies. */
  agreement: string | null
  standardUsdPerOutputSecond: number
  standardUsdPer15Seconds: number
  videoReferenceUsdPerCombinedSecond: number
  /** `standard` when the agreement does not price reference-to-video. */
  videoReferenceSource: 'agreement' | 'standard'
}

export interface EnterpriseAgreement {
  includedSeats: number
  seatsUsed: number
  mcpUsage: McpUsageResponse | null
  seedanceRateCard: EnterpriseAgreementRate[]
}

export async function getEnterpriseAgreement(orgId: string): Promise<EnterpriseAgreement | null> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/enterprise-agreement`)
  if (res.status === 403 || res.status === 404) return null
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error ?? 'Failed to fetch enterprise agreement')
  }
  return res.json()
}

export async function fetchUpscalePresets(
  orgId: string,
  type: UpscalePresetType,
): Promise<{ presets: UpscalePreset[]; isOrgAdmin: boolean }> {
  const params = new URLSearchParams({ orgId, type })
  const res = await fetchWithAuth(`${apiUrl()}/upscale-presets?${params.toString()}`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to fetch upscale presets')
  }

  return res.json()
}

export async function createUpscalePreset(params: {
  organizationId: string
  name: string
  type: UpscalePresetType
  settings: Record<string, unknown>
}): Promise<UpscalePreset> {
  const res = await fetchWithAuth(`${apiUrl()}/upscale-presets`, {
    method: 'POST',
    body: JSON.stringify(params),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to create upscale preset')
  }

  const data = (await res.json()) as { preset: UpscalePreset }
  return data.preset
}

export async function updateUpscalePreset(
  presetId: string,
  updates: {
    name?: string
    settings?: Record<string, unknown>
  },
): Promise<UpscalePreset> {
  const res = await fetchWithAuth(`${apiUrl()}/upscale-presets/${presetId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to update upscale preset')
  }

  const data = (await res.json()) as { preset: UpscalePreset }
  return data.preset
}

export async function deleteUpscalePreset(presetId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/upscale-presets/${presetId}`, {
    method: 'DELETE',
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to delete upscale preset')
  }
}

/**
 * Update spend cap and alert threshold for a project.
 * Only team admins can update spend caps.
 */
export async function updateProjectSpendCaps(
  projectId: string,
  data: { spendCap?: number | null; alertThreshold?: number | null },
): Promise<{ success: boolean; spendCap: number | null; alertThreshold: number | null }> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/spend-caps`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to update spend caps')
  }

  return res.json()
}

/** Read Dub's dub_id click cookie (set by @dub/analytics in RootShell). */
function readDubClickIdCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(/(?:^|;\s*)dub_id=([^;]+)/)
  return match?.[1] ? decodeURIComponent(match[1]) : undefined
}

export class CompleteConversionError extends Error {
  code: string
  status: number

  constructor(message: string, code: string, status: number) {
    super(message)
    this.name = 'CompleteConversionError'
    this.code = code
    this.status = status
  }
}

/**
 * Complete user conversion from anonymous to real account.
 * Atomically handles profile update, org creation, and referral redemption.
 * This replaces the separate calls to update profile + createBillingOrg + redeemReferral.
 */
export async function completeConversion(data: {
  email: string
  name?: string
  referralCode?: string
  /** Where the stored ?ref= was captured: shared project link, marketing page,
   * motion share, or an AI-assistant referral sentinel. */
  referralSource?: ReferralSource
  referralLandingPath?: string
  promoSlug?: string
  method?: 'google' | 'email' | 'unknown'
  visitorId?: string | null
  userLocale?: AppLocale
}): Promise<{
  organizationId: string
  referralRedeemed: boolean
  referralError?: string
  isNewSignup?: boolean
}> {
  const res = await fetchWithAuth(`${apiUrl()}/auth/complete-conversion`, {
    method: 'POST',
    // dub_id is set by the Dub analytics script when the visitor arrived via a
    // partner short link; threading it through lets the backend bind the
    // affiliate lead to Dub's click chain. Absent for non-affiliate signups.
    body: JSON.stringify({ ...data, dubClickId: readDubClickIdCookie() }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new CompleteConversionError(
      error.message || error.error || 'Failed to complete conversion',
      error.code || error.error || 'UNKNOWN',
      res.status,
    )
  }

  const result = (await res.json()) as {
    organizationId: string | null
    referralRedeemed: boolean
    referralError?: string
    isNewSignup?: boolean
  }

  if (!result.organizationId) {
    throw new CompleteConversionError(
      'Your account was created, but setup did not finish. Please try again.',
      'ACCOUNT_SETUP_FAILED',
      res.status,
    )
  }

  return {
    ...result,
    organizationId: result.organizationId,
  }
}

// =============================================================================
// Email OTP Verification
// =============================================================================

export type SendOTPError =
  'EMAIL_ALREADY_REGISTERED' | 'EMAIL_DOMAIN_BLOCKED' | 'RATE_LIMITED' | 'TURNSTILE_FAILED' | 'UNKNOWN'
export type VerifyOTPError = 'INVALID_OTP' | 'OTP_EXPIRED' | 'TOO_MANY_ATTEMPTS' | 'NOT_FOUND' | 'UNKNOWN'
export type PasswordPolicyScope = 'standard' | 'enterprise'
export interface PasswordPolicy {
  minLength: number
  scope: PasswordPolicyScope
  requireLowercase: boolean
  requireUppercase: boolean
  requireNumber: boolean
  requireSymbol: boolean
  enterpriseSessionInactivityTimeoutMs: number | null
}

export type CompleteSignupError =
  | 'INVALID_TOKEN'
  | 'EMAIL_MISMATCH'
  | 'ALREADY_UPGRADED'
  | 'PASSWORD_REQUIRED'
  | 'PASSWORD_TOO_SHORT'
  | 'PASSWORD_UPDATE_FAILED'
  | 'UNKNOWN'

export type ChangePasswordErrorCode = 'PASSWORD_REQUIRED' | 'PASSWORD_TOO_SHORT' | 'PASSWORD_UPDATE_FAILED' | 'UNKNOWN'

export class ChangePasswordError extends Error {
  code: ChangePasswordErrorCode
  minLength?: number

  constructor(message: string, code: ChangePasswordErrorCode, minLength?: number) {
    super(message)
    this.name = 'ChangePasswordError'
    this.code = code
    this.minLength = minLength
  }
}

/**
 * Send OTP to email for verification.
 */
export async function sendOTP(
  email: string,
  captchaToken: string | null,
  locale?: string,
): Promise<{ success: true } | { success: false; error: SendOTPError; message: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/auth/send-otp`, {
    method: 'POST',
    body: JSON.stringify({ email, captchaToken, locale }),
  })

  if (!res.ok) {
    const error = await res.json()
    return {
      success: false,
      error: error.error || 'UNKNOWN',
      message: error.message || 'Failed to send verification code',
    }
  }

  return { success: true }
}

/**
 * Verify OTP code and get verification token.
 */
export async function verifyOTPCode(
  email: string,
  otp: string,
): Promise<{ success: true; verificationToken: string } | { success: false; error: VerifyOTPError; message: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/auth/verify-otp`, {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  })

  if (!res.ok) {
    const error = await res.json()
    return { success: false, error: error.error || 'UNKNOWN', message: error.message || 'Failed to verify code' }
  }

  return res.json()
}

/**
 * Get the password policy for the current account, or for a signup email.
 */
export async function getPasswordPolicy(email?: string, verificationToken?: string): Promise<PasswordPolicy> {
  const params = new URLSearchParams()
  if (email) params.set('email', email)
  if (verificationToken) params.set('verificationToken', verificationToken)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await fetchWithAuth(`${apiUrl()}/auth/password-policy${qs}`)

  if (!res.ok) {
    return STANDARD_PASSWORD_POLICY
  }

  return res.json()
}

/** Frontend mirror of the backend standard self-serve policy; used as a fallback. */
export const STANDARD_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 6,
  scope: 'standard',
  requireLowercase: false,
  requireUppercase: false,
  requireNumber: false,
  requireSymbol: false,
  enterpriseSessionInactivityTimeoutMs: null,
}

/**
 * Frontend mirror of the backend fixed Enterprise policy (see
 * backend/src/auth/passwordPolicy.ts `getEnterprisePasswordPolicy`). Used as the
 * fallback wherever the caller is known to be Enterprise-scoped (e.g. the reset
 * gate), so a failed policy fetch never under-validates and lets a password
 * through that the backend will reject.
 */
export const ENTERPRISE_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 14,
  scope: 'enterprise',
  requireLowercase: true,
  requireUppercase: true,
  requireNumber: true,
  requireSymbol: true,
  enterpriseSessionInactivityTimeoutMs: null,
}

/**
 * Whether the current user must reset their password to satisfy the Enterprise
 * password policy. Falls back to not-required if the check can't be reached, so
 * a transient error never traps the user behind a mandatory reset.
 */
export async function getPasswordCompliance(): Promise<{ resetRequired: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/auth/password-compliance`)
  if (!res.ok) {
    return { resetRequired: false }
  }
  return res.json()
}

export interface SsoResolveResult {
  ssoEnabled: boolean
  providerId?: string | null
  orgName?: string | null
}

/**
 * Home Realm Discovery: ask the backend whether an email's domain is SSO-enabled.
 * Only the domain is sent — the full address would leak PII into request logs
 * and the backend's Sentry request context. Never throws — returns
 * { ssoEnabled: false } on any failure so a transient issue falls back to the
 * normal password/Google flow rather than blocking login. The endpoint is
 * public and MUST be called with plain fetch, not fetchWithAuth: on SSO-only
 * deployments the caller has no session at all, and fetchWithAuth throws
 * before the network call without a token — which would make HRD always
 * report "not SSO" and hide the only working sign-in path.
 */
export async function resolveSso(email: string): Promise<SsoResolveResult> {
  const domain = email.split('@')[1]?.trim().toLowerCase() ?? ''
  if (!domain) {
    return { ssoEnabled: false }
  }
  try {
    const res = await fetch(`${apiUrl()}/auth/sso/resolve?domain=${encodeURIComponent(domain)}`)
    if (!res.ok) {
      return { ssoEnabled: false }
    }
    return await res.json()
  } catch {
    return { ssoEnabled: false }
  }
}

/**
 * Complete signup after email verification.
 * Validates the verification token and applies Supabase credentials server-side.
 */
export async function completeEmailSignup(
  email: string,
  verificationToken: string,
  password: string,
): Promise<{ success: true } | { success: false; error: CompleteSignupError; message: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/auth/complete-signup`, {
    method: 'POST',
    body: JSON.stringify({ email, verificationToken, password }),
  })

  if (!res.ok) {
    const error = await res.json()
    return { success: false, error: error.error || 'UNKNOWN', message: error.message || 'Failed to complete signup' }
  }

  return res.json()
}

/**
 * Change the current user's password through the backend so enterprise-scoped
 * accounts can enforce a stricter policy than Supabase's global minimum.
 */
export async function changePassword(password: string, currentPassword?: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/auth/change-password`, {
    method: 'POST',
    body: JSON.stringify({ password, ...(currentPassword ? { currentPassword } : {}) }),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new ChangePasswordError(
      error.message || error.error || 'Failed to update password',
      error.error || 'UNKNOWN',
      typeof error.minLength === 'number' ? error.minLength : undefined,
    )
  }
}

/**
 * Check if an error is an insufficient credits error.
 */
export function isInsufficientCreditsError(error: unknown): error is Error & { billingInfo: InsufficientCreditsError } {
  if (error instanceof Error && 'billingInfo' in error) {
    const info = (error as { billingInfo?: unknown }).billingInfo
    return (
      info !== undefined &&
      typeof info === 'object' &&
      info !== null &&
      'code' in info &&
      info.code === 'INSUFFICIENT_CREDITS'
    )
  }
  return false
}

export interface CreditTransaction {
  id: string
  organization_id: string
  amount_olives: number
  type: 'generation' | 'agent_work' | 'export' | 'topup' | 'refund' | 'adjustment' | 'signup_bonus' | 'transfer'
  billing_mode?: 'personal_tab' | 'team_tab' | 'generator_pays'
  generation_id?: string
  user_id?: string
  project_id?: string | null
  idempotency_key?: string
  provider_key_source?: 'martini' | 'byok' | null
  provider_cost_usd?: number | string | null
  provider_cost_source?: 'estimated' | 'reconciled' | null
  description?: string
  created_at: string
  generations?: { model: string | null } | null
  profiles?: { alias: string; color: string } | null
}

/**
 * Get credit transactions for an organization.
 */
export async function getCreditTransactions(
  orgId: string,
  options?: { limit?: number; offset?: number },
): Promise<{ transactions: CreditTransaction[]; total: number }> {
  const params = new URLSearchParams({
    org_id: orgId,
    limit: String(options?.limit ?? 50),
    offset: String(options?.offset ?? 0),
  })

  const res = await fetchWithAuth(`${apiUrl()}/billing/transactions?${params}`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to get transactions')
  }

  return res.json()
}

// =============================================================================
// Invoices API
// =============================================================================

/**
 * Get invoices for an organization. Admin-only for teams.
 */
export async function getInvoices(orgId: string): Promise<{ invoices: NormalizedInvoice[] }> {
  const params = new URLSearchParams({ org_id: orgId })
  const res = await fetchWithAuth(`${apiUrl()}/billing/invoices?${params}`)

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to load invoices')
  }

  return res.json()
}

/**
 * Fetch a fresh Stripe PDF URL for an invoice. Backend generates a new link on each call
 * so users always get a non-expired download.
 */
export async function getInvoiceDownloadUrl(orgId: string, stripeInvoiceId: string): Promise<string> {
  const params = new URLSearchParams({ org_id: orgId })
  const res = await fetchWithAuth(
    `${apiUrl()}/billing/invoices/${encodeURIComponent(stripeInvoiceId)}/download?${params}`,
  )

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to generate invoice link')
  }

  const data = await res.json()
  return data.url as string
}

// =============================================================================
// Spend by Project API
// =============================================================================

export interface MemberSpend {
  userId: string
  alias: string
  color: string
  totalOlives: number
  transactionCount: number
}

export interface ProjectSpendSummary {
  projectId: string
  projectName: string
  totalOlives: number
  transactionCount: number
  members?: MemberSpend[]
  spendCap: number | null
  spendAlertThreshold: number | null
}

export interface SpendByProjectResponse {
  projects: ProjectSpendSummary[]
}

/**
 * Get per-project spend aggregation for an organization.
 */
export async function getSpendByProject(
  orgId: string,
  options?: { from?: string; to?: string },
): Promise<SpendByProjectResponse> {
  const params = new URLSearchParams({ org_id: orgId })
  if (options?.from) params.set('from', options.from)
  if (options?.to) params.set('to', options.to)

  const res = await fetchWithAuth(`${apiUrl()}/billing/spend-by-project?${params}`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to get spend by project')
  }

  return res.json()
}

// =============================================================================
// Spend analytics + per-member caps (admin dashboard)
// =============================================================================

export interface SpendAnalyticsParams {
  orgId: string
  projectId?: string | null
  from?: string | null
  to?: string | null
  groupBy: SpendGroupBy
  bucket?: SpendBucket | null
  filters?: {
    projectIds?: string[]
    actorIds?: string[]
    userIds?: string[]
    models?: string[]
    types?: string[]
  }
}

export async function getSpendAnalytics(params: SpendAnalyticsParams): Promise<SpendAnalyticsResponse> {
  const qp = new URLSearchParams({ org_id: params.orgId, group_by: params.groupBy })
  if (params.projectId) qp.set('project_id', params.projectId)
  if (params.from) qp.set('from', params.from)
  if (params.to) qp.set('to', params.to)
  if (params.bucket) qp.set('bucket', params.bucket)
  if (params.filters?.projectIds?.length) qp.set('project_ids', params.filters.projectIds.join(','))
  if (params.filters?.actorIds?.length) qp.set('actor_ids', params.filters.actorIds.join(','))
  if (params.filters?.userIds?.length) qp.set('user_ids', params.filters.userIds.join(','))
  if (params.filters?.models?.length) qp.set('models', params.filters.models.join(','))
  if (params.filters?.types?.length) qp.set('types', params.filters.types.join(','))

  const res = await fetchWithAuth(`${apiUrl()}/billing/analytics?${qp}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || 'Failed to load spend analytics')
  }
  return res.json()
}

export async function getMemberSpendCaps(orgId: string): Promise<MemberSpendCapsResponse> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/members/caps`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || 'Failed to load member caps')
  }
  return res.json()
}

export async function updateMemberSpendCap(
  orgId: string,
  userId: string,
  body: { spendCap: number | null; alertThreshold: number | null },
): Promise<{ ok: true }> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${orgId}/members/${userId}/caps`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || 'Failed to update member cap')
  }
  return res.json()
}

/**
 * Create a checkout session for purchasing olives.
 * Returns the checkout URL to redirect to.
 */
export async function createTopupCheckout(
  quantity: number,
  organizationId?: string,
  returnUrl?: string,
  /** UI surface that started this checkout — attributes the completed top-up. */
  source?: string,
): Promise<{ checkout_url: string }> {
  // Abort if checkout takes longer than 30 seconds (Autumn/Stripe can be slow)
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 30_000)

  try {
    const res = await fetchWithAuth(`${apiUrl()}/billing/checkout`, {
      method: 'POST',
      signal: controller.signal,
      body: JSON.stringify({
        quantity,
        ...(organizationId ? { organizationId } : {}),
        ...(returnUrl ? { returnUrl } : {}),
        ...(source ? { source } : {}),
      }),
    })

    if (!res.ok) {
      const error = await res.json()
      throw new Error(error.error || error.message || 'Failed to create checkout')
    }

    const data = await res.json()

    if (!data.checkout_url) {
      captureSentryError(new Error('Checkout returned empty URL'), {
        operation: 'topup_checkout_missing_url',
        extra: { quantity, organizationId, responseData: data },
      })
      throw new Error('No checkout URL returned. Please try again.')
    }

    return data
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      captureSentryError(new Error('Checkout request timed out after 30s'), {
        operation: 'topup_checkout_timeout',
        extra: { quantity, organizationId },
      })
      throw new Error('Checkout request timed out. Please try again.')
    }
    throw err
  } finally {
    clearTimeout(timeout)
  }
}

export type TopupPreview =
  | {
      mode: 'instant'
      total_usd: number | null
      currency: string | null
      /** Saved card identification for the confirm sheet; null when unavailable */
      card?: { brand: string | null; last4: string | null } | null
    }
  | { mode: 'redirect'; checkout_url: string }

/**
 * Price a top-up against the saved card. mode 'instant' means the card on
 * file can be charged directly (1-click rebuy, confirm via confirmTopup);
 * mode 'redirect' carries a ready-to-use Stripe checkout URL.
 */
export async function previewTopup(
  quantity: number,
  organizationId?: string,
  returnUrl?: string,
  /** UI surface — attributes the completed top-up on the no-saved-card redirect path. */
  source?: string,
): Promise<TopupPreview> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 30_000)

  try {
    const res = await fetchWithAuth(`${apiUrl()}/billing/topup/preview`, {
      method: 'POST',
      signal: controller.signal,
      body: JSON.stringify({
        quantity,
        ...(organizationId ? { organizationId } : {}),
        ...(returnUrl ? { returnUrl } : {}),
        ...(source ? { source } : {}),
      }),
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({}))
      throw new Error(error.error || error.message || 'Failed to preview top-up')
    }

    return res.json()
  } finally {
    clearTimeout(timeout)
  }
}

export type ConfirmTopupErrorCode = 'card_required' | 'payment_declined' | 'pending'

export class ConfirmTopupError extends Error {
  constructor(
    message: string,
    public readonly code?: ConfirmTopupErrorCode,
  ) {
    super(message)
    this.name = 'ConfirmTopupError'
  }
}

/**
 * Charge the saved card for a top-up (1-click rebuy). confirmToken is a
 * client-generated UUID — the backend charges each token exactly once.
 * Throws ConfirmTopupError with code 'card_required' when no chargeable card
 * exists (caller falls back to redirect checkout).
 */
export async function confirmTopup(
  quantity: number,
  confirmToken: string,
  organizationId?: string,
  /** UI surface that started this rebuy — attributes the completed top-up. */
  source?: string,
): Promise<{ success: boolean; already_processed?: boolean }> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 30_000)

  try {
    const res = await fetchWithAuth(`${apiUrl()}/billing/topup/confirm`, {
      method: 'POST',
      signal: controller.signal,
      body: JSON.stringify({
        quantity,
        confirmToken,
        ...(organizationId ? { organizationId } : {}),
        ...(source ? { source } : {}),
      }),
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({}))
      throw new ConfirmTopupError(error.error || error.message || 'Failed to complete purchase', error.code)
    }

    return res.json()
  } finally {
    clearTimeout(timeout)
  }
}

/**
 * Log a successful topup transaction.
 * Called after returning from Stripe checkout to record the transaction.
 * The backend validates the session against Stripe before crediting.
 */
export async function logTopupTransaction(sessionId: string): Promise<{ success: boolean; already_logged: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/billing/topup/log`, {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to log topup')
  }

  return res.json()
}

/** Auto top-up state for the billing page (config lives on the Autumn customer). */
export interface AutoTopupState {
  /** Billing org the state belongs to (personal org resolved server-side). */
  orgId: string
  /** null when the org has never configured auto top-up. */
  config: AutoTopupConfig | null
  card: { brand: string | null; last4: string | null } | null
  /** Plan-based defaults for prefilling the enable UI. */
  defaults: AutoTopupConfig
}

export async function getAutoTopup(organizationId?: string): Promise<AutoTopupState> {
  const params = organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : ''
  const res = await fetchWithAuth(`${apiUrl()}/billing/auto-topup${params}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to load auto top-up settings')
  }
  return res.json()
}

export class UpdateAutoTopupError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message)
    this.name = 'UpdateAutoTopupError'
  }
}

// Rolling-deploy compatibility only: the previous backend requires this field.
// The new backend ignores it and writes an uncapped Autumn configuration.
const LEGACY_AUTO_TOPUP_MAX_PER_MONTH = 50

function autoTopupTransportConfig(config: AutoTopupConfig): AutoTopupConfig & { maxPerMonth: number } {
  return { ...config, maxPerMonth: LEGACY_AUTO_TOPUP_MAX_PER_MONTH }
}

/** Save the org's auto top-up config. */
export async function updateAutoTopup(
  config: AutoTopupConfig,
  organizationId?: string,
  /** UI surface that changed the setting — attributes the analytics event. */
  source?: string,
): Promise<{ success: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/billing/auto-topup`, {
    method: 'PUT',
    body: JSON.stringify({
      config: autoTopupTransportConfig(config),
      ...(organizationId ? { organizationId } : {}),
      ...(source ? { source } : {}),
    }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new UpdateAutoTopupError(
      error.error || 'Failed to save auto top-up settings',
      typeof error.code === 'string' ? error.code : undefined,
    )
  }
  return res.json()
}

/**
 * Start a hosted card-setup session (saves a card without purchasing).
 * Passing autoTopup carries the intended config through the hosted card-save
 * flow so the billing page can enable auto top-up after the user returns.
 */
export async function createCardSetup(options?: {
  organizationId?: string
  returnUrl?: string
  autoTopup?: AutoTopupConfig
}): Promise<{ url: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/billing/card-setup`, {
    method: 'POST',
    body: JSON.stringify({
      ...(options?.organizationId ? { organizationId: options.organizationId } : {}),
      ...(options?.returnUrl ? { returnUrl: options.returnUrl } : {}),
      ...(options?.autoTopup ? { autoTopup: autoTopupTransportConfig(options.autoTopup) } : {}),
    }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to start card setup')
  }
  return res.json()
}

/** Check whether Autumn has a Stripe customer portal for this organization. */
export async function getBillingPortalAvailability(organizationId?: string): Promise<{ available: boolean }> {
  const params = new URLSearchParams()
  if (organizationId) params.set('organizationId', organizationId)
  const query = params.size ? `?${params}` : ''
  const res = await fetchWithAuth(`${apiUrl()}/billing/portal${query}`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to check billing portal availability')
  }
  return res.json()
}

/** Open the Stripe customer portal generated through Autumn. */
export async function openBillingPortal(options?: {
  organizationId?: string
  returnUrl?: string
}): Promise<{ url: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/billing/portal`, {
    method: 'POST',
    body: JSON.stringify({
      ...(options?.organizationId ? { organizationId: options.organizationId } : {}),
      ...(options?.returnUrl ? { returnUrl: options.returnUrl } : {}),
    }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to open billing portal')
  }
  return res.json()
}

// =============================================================================
// Referral API
// =============================================================================

/**
 * Get the referrer info for a referral code (public endpoint).
 */
export async function getReferrer(code: string): Promise<{ alias: string; color: string } | null> {
  const res = await fetch(`${apiUrl()}/referral/referrer/${encodeURIComponent(code)}`)

  if (!res.ok) {
    if (res.status === 404) {
      return null
    }
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to get referrer')
  }

  return res.json()
}

/**
 * Get the current user's referral code.
 */
export async function getReferralCode(): Promise<{ code: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/referral/code`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to get referral code')
  }

  return res.json()
}

/**
 * Redeem a referral code after signup.
 */
export async function redeemReferralCode(
  code: string,
): Promise<{ success: boolean; rewardId?: string; error?: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/referral/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to redeem referral code')
  }

  return res.json()
}

/**
 * Get referral status for the current user.
 */
export async function getReferralStatus(): Promise<{
  code: string | null
  referrals: Array<{
    alias: string
    color: string
    createdAt: string
    status: 'completed' | 'pending' | 'not_awarded'
  }>
  totalCreditsEarned: number
  referralCap: number | null
  isUncapped: boolean
}> {
  const res = await fetchWithAuth(`${apiUrl()}/referral/status`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || error.message || 'Failed to get referral status')
  }

  return res.json()
}

// =============================================================================
// Promo Campaigns API
// =============================================================================

export interface CampaignReward {
  bonus_olives: number
}

export interface CampaignRewards {
  [planId: string]: CampaignReward
}

export interface PromoCampaignPublic {
  slug: string
  active: boolean
  landing_config: {
    headline?: string
    description?: string
    avatar_url?: string
    inviter_name?: string
  } | null
  rewards: CampaignRewards
  eligible_plans: string[]
  expires_at: string | null
  /** Whether the 50%-off-first-month promo is live (campaign visitors are new
   * users, so landing surfaces may advertise the strike price). Absent = off. */
  firstMonthPromoActive?: boolean
}

export interface ActivePromo {
  slug: string
  campaign_id: string
  inviter_name: string | null
  rewards: CampaignRewards
  dismissed: boolean
  expires_at: string | null
}

/**
 * Look up a promo campaign by slug (public, no auth required).
 */
export async function getPromoCampaign(slug: string): Promise<PromoCampaignPublic | null> {
  const res = await fetch(`${apiUrl()}/promo/${encodeURIComponent(slug)}`)

  if (!res.ok) {
    if (res.status === 404) return null
    return null
  }

  return res.json()
}

/**
 * Record promo attribution (authenticated user visited a promo link).
 */
export async function recordPromoAttribution(
  slug: string,
  source: 'landing_page' | 'invite_link' | 'project_link' | 'manual' = 'landing_page',
): Promise<void> {
  try {
    await fetchWithAuth(`${apiUrl()}/promo/${encodeURIComponent(slug)}/attribute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source }),
    })
  } catch (err) {
    captureSentryError(err, { operation: 'recordPromoAttribution', tags: { slug, source } })
  }
}

/**
 * Get the current user's active (unredeemed) promo campaign.
 */
export async function getActivePromo(): Promise<ActivePromo | null> {
  const res = await fetchWithAuth(`${apiUrl()}/promo/me`)
  if (!res.ok) return null
  const data = await res.json()
  return data.promo ?? null
}

/**
 * Dismiss the promo canvas banner.
 */
export async function dismissActivePromo(): Promise<void> {
  await fetchWithAuth(`${apiUrl()}/promo/me/dismiss`, { method: 'POST' }).catch(() => {})
}

// =============================================================================
// Teams API
// =============================================================================

export interface Team {
  id: string
  name: string
  slug: string
}

export interface TeamMember {
  userId: string
  alias: string
  email: string
  role: OrganizationRole
  joinedAt: string
}

export interface TeamInvitation {
  id: string
  email: string
  role: string
  status: string
  createdAt: string
  expiresAt: string
  token?: string // Only in development
}

export interface PendingInvitation {
  id: string
  teamName: string
  teamSlug: string
  role: string
  createdAt: string
  invitedByAlias: string
}

export interface PendingInvitationActionResult {
  teamId: string
  teamSlug: string
  teamName: string
  role: OrganizationRole
}

/**
 * Create a new team.
 */
export async function createTeam(name: string): Promise<Team> {
  const res = await fetchWithAuth(`${apiUrl()}/teams`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Failed to create team' }))
    // Provide more specific error messages
    const errorMessage = error.error || error.message || 'Failed to create team'
    const errorWithContext: Error & { status?: number } = new Error(errorMessage)
    // Preserve status code for better error handling
    errorWithContext.status = res.status
    throw errorWithContext
  }

  return res.json()
}

/**
 * Update a team's settings.
 */
export async function updateTeamApi(
  teamId: string,
  updates: { name?: string; billingEmail?: string; defaultProjectBillingMode?: 'team_tab' | 'generator_pays' },
): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to update team')
  }
}

export async function updateTeamEnterpriseSecurity(
  teamId: string,
  updates: {
    passwordPolicyEnabled?: boolean
    sessionInactivityTimeoutEnabled?: boolean
  },
): Promise<{
  passwordPolicyEnabled: boolean
  sessionInactivityTimeoutEnabled: boolean
}> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}/enterprise-security`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to update enterprise security settings')
  }

  return res.json()
}

/**
 * Get disabled models and resolutions for a team/org.
 */
export async function getTeamModelPolicy(teamId: string): Promise<{
  disabledModelIds: string[]
  disabledResolutionsByModel: Record<string, string[]>
}> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}/disabled-models`)
  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to fetch disabled models')
  }
  const data = await res.json()
  return {
    disabledModelIds: data.disabledModelIds ?? [],
    disabledResolutionsByModel: data.disabledResolutionsByModel ?? {},
  }
}

/**
 * Set disabled models and resolutions for a team/org. Admin only.
 */
export async function setTeamModelPolicy(
  teamId: string,
  policy: { modelIds: string[]; disabledResolutionsByModel: Record<string, string[]> },
): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}/disabled-models`, {
    method: 'PUT',
    body: JSON.stringify(policy),
  })
  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to update disabled models')
  }
}

/**
 * Get the current user's hidden model IDs.
 */
export async function getUserHiddenModels(): Promise<string[]> {
  const res = await fetchWithAuth(`${apiUrl()}/account/hidden-models`)
  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to fetch hidden models')
  }
  const data = await res.json()
  return data.hiddenModelIds
}

/**
 * Set the current user's hidden model IDs (full replace).
 */
export async function setUserHiddenModels(modelIds: string[]): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/account/hidden-models`, {
    method: 'PUT',
    body: JSON.stringify({ modelIds }),
  })
  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to update hidden models')
  }
}

/**
 * Delete a team (soft-delete). All projects must be removed first.
 */
export async function deleteTeamApi(teamId: string): Promise<{ balanceTransferred: number }> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}`, {
    method: 'DELETE',
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to delete team')
  }

  const data = await res.json()
  return { balanceTransferred: data.balanceTransferred ?? 0 }
}

/**
 * Invite a user to a team by email.
 */
export async function createTeamInvitation(
  teamId: string,
  email: string,
  role: OrganizationRole = 'member',
): Promise<TeamInvitation> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}/invitations`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to create invitation')
  }

  return res.json()
}

/**
 * List pending invitations for a team.
 */
export async function listTeamInvitations(teamId: string): Promise<{ invitations: TeamInvitation[] }> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}/invitations`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to list invitations')
  }

  return res.json()
}

/**
 * Revoke a pending invitation.
 */
export async function revokeTeamInvitation(teamId: string, invitationId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}/invitations/${invitationId}`, {
    method: 'DELETE',
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to revoke invitation')
  }
}

/**
 * Preview a team invitation by token (public, no auth required).
 * Returns team name, inviter name, and invited email for display context.
 */
export async function previewTeamInvitation(
  token: string,
): Promise<{ teamName: string; inviterName: string; emailHint: string }> {
  const res = await fetch(`${apiUrl()}/invitations/${token}/preview`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to preview invitation')
  }

  return res.json()
}

/**
 * Accept a team invitation by token.
 */
export async function acceptTeamInvitation(
  token: string,
): Promise<{ teamId: string; teamSlug: string; teamName: string; role: OrganizationRole }> {
  const res = await fetchWithAuth(`${apiUrl()}/invitations/${token}/accept`, {
    method: 'POST',
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to accept invitation')
  }

  return res.json()
}

/**
 * Get pending invitations for the current user.
 */
export async function getPendingInvitations(): Promise<{ invitations: PendingInvitation[] }> {
  const res = await fetchWithAuth(`${apiUrl()}/invitations/pending`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to get pending invitations')
  }

  return res.json()
}

export async function acceptPendingTeamInvitation(invitationId: string): Promise<PendingInvitationActionResult> {
  const encodedInvitationId = encodeURIComponent(invitationId)
  const res = await fetchWithAuth(`${apiUrl()}/invitations/pending/${encodedInvitationId}/accept`, {
    method: 'POST',
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to accept invitation')
  }

  return res.json()
}

export async function declinePendingTeamInvitation(invitationId: string): Promise<void> {
  const encodedInvitationId = encodeURIComponent(invitationId)
  const res = await fetchWithAuth(`${apiUrl()}/invitations/pending/${encodedInvitationId}/decline`, {
    method: 'POST',
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to decline invitation')
  }
}

/**
 * List members of a team.
 */
export async function listTeamMembers(teamId: string): Promise<{ members: TeamMember[] }> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}/members`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to list team members')
  }

  return res.json()
}

/**
 * Update a team member's role.
 */
export async function updateTeamMemberRole(teamId: string, userId: string, role: OrganizationRole): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}/members/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to update member role')
  }
}

/**
 * Remove a member from a team (or leave).
 */
export async function removeTeamMember(teamId: string, userId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/${teamId}/members/${userId}`, {
    method: 'DELETE',
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to remove team member')
  }
}

/**
 * Assign a project to a team.
 */
export async function assignProjectToTeam(
  projectId: string,
  teamId: string,
  // Omitted → the backend applies the team's default project billing mode.
  billingMode?: 'team_tab' | 'generator_pays',
): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/team`, {
    method: 'PATCH',
    body: JSON.stringify(billingMode ? { teamId, billingMode } : { teamId }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to assign project to team')
  }
}

/**
 * Remove a project from a team (return to personal).
 */
export async function removeProjectFromTeam(projectId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/team`, {
    method: 'DELETE',
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to remove project from team')
  }
}

/**
 * Create a project via the backend API.
 * Returns { id, name, createdAt, visibility, teamId }.
 */
export async function createProjectViaBackend(opts?: { name?: string; teamId?: string }): Promise<{
  id: string
  name: string
  createdAt: string
  visibility: string
  teamId: string | null
}> {
  const res = await fetchWithAuth(`${apiUrl()}/projects`, {
    method: 'POST',
    body: JSON.stringify({ name: opts?.name, teamId: opts?.teamId }),
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to create project')
  }

  return res.json()
}

// ============================================================================
// Project organization: per-user folders + reordering (ENG-245)
// ============================================================================

export interface ProjectFolderDTO {
  id: string
  name: string
  position: number
  createdAt: string
}

/** Create a folder for the current user. */
export async function createFolderViaBackend(name: string): Promise<ProjectFolderDTO> {
  const res = await fetchWithAuth(`${apiUrl()}/folders`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to create folder')
  }

  return res.json()
}

/** Rename and/or reposition a folder. */
export async function updateFolderViaBackend(
  folderId: string,
  updates: { name?: string; position?: number },
): Promise<ProjectFolderDTO> {
  const res = await fetchWithAuth(`${apiUrl()}/folders/${folderId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to update folder')
  }

  return res.json()
}

/** Delete a folder; its projects fall back to ungrouped. */
export async function deleteFolderViaBackend(folderId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/folders/${folderId}`, {
    method: 'DELETE',
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to delete folder')
  }
}

/**
 * Move a project into a folder and/or set its sort position for the current
 * user. Pass folderId null for ungrouped; omit sortPosition to append to end.
 */
export async function setProjectOrganizationViaBackend(
  projectId: string,
  organization: { folderId: string | null; sortPosition?: number | null },
): Promise<{ folderId: string | null; sortPosition: number | null }> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/organization`, {
    method: 'PATCH',
    body: JSON.stringify({ folderId: organization.folderId, sortPosition: organization.sortPosition }),
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to update project organization')
  }

  return res.json()
}

/**
 * Bulk-assign folder + sort position for several projects in one request.
 * Drives drag-reorder, which renumbers a whole bucket on drop.
 */
export async function bulkSetProjectOrganizationViaBackend(
  items: Array<{ projectId: string; folderId: string | null; sortPosition: number }>,
): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/organization`, {
    method: 'PATCH',
    body: JSON.stringify({ items }),
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to update project organization')
  }
}

// ============================================================================
// Team project organization: SHARED per-team folders + reordering (ENG-245)
// ============================================================================

/** Create a shared folder for a team. */
export async function createTeamFolderViaBackend(teamId: string, name: string): Promise<ProjectFolderDTO> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${teamId}/folders`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to create team folder')
  }

  return res.json()
}

/** Rename and/or reposition a shared team folder. */
export async function updateTeamFolderViaBackend(
  teamId: string,
  folderId: string,
  updates: { name?: string; position?: number },
): Promise<ProjectFolderDTO> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${teamId}/folders/${folderId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to update team folder')
  }

  return res.json()
}

/** Delete a shared team folder; its projects fall back to ungrouped. */
export async function deleteTeamFolderViaBackend(teamId: string, folderId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${teamId}/folders/${folderId}`, {
    method: 'DELETE',
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to delete team folder')
  }
}

/**
 * Bulk-assign folder + sort position for several of a team's projects in one
 * request. Drives drag-reorder, which renumbers a whole bucket on drop. The
 * ordering is shared across the team.
 */
export async function bulkSetTeamProjectOrganizationViaBackend(
  teamId: string,
  items: Array<{ projectId: string; folderId: string | null; sortPosition: number }>,
): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/organizations/${teamId}/projects/organization`, {
    method: 'PATCH',
    body: JSON.stringify({ items }),
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to update team project organization')
  }
}

/**
 * Atomically claim quick-start project creation for the current user.
 */
export async function claimQuickStartProjectViaBackend(): Promise<{
  canCreate: boolean
  reason?: 'already_has_projects' | 'already_claimed'
}> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/quick-start/claim`, {
    method: 'POST',
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to claim quick-start project creation')
  }

  return res.json()
}

/**
 * Release quick-start claim if creation failed.
 */
export async function releaseQuickStartProjectViaBackend(): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/quick-start/release`, {
    method: 'POST',
  })

  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to release quick-start project creation')
  }
}

export async function joinProjectViaBackend(projectId: string): Promise<{
  id: string
  name: string
  createdAt: string
  visibility: string
  teamId: string | null
  role: string
  joinedAt: string
} | null> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/join`, {
    method: 'POST',
  })

  if (!res.ok) {
    const data = await res.json()
    // Return null for expected failures (not_found, locked) — caller handles them
    if (res.status === 404 || res.status === 403) return null
    throw new Error(data.error || 'Failed to join project')
  }

  return res.json()
}

export async function setProjectVisibilityViaBackend(
  projectId: string,
  visibility: 'private' | 'link_view' | 'link_edit',
): Promise<{ success: boolean; error?: string; code?: string }> {
  try {
    const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ visibility }),
    })

    const data = await res.json()

    if (!res.ok) {
      return { success: false, error: data.error || 'Failed to update visibility', code: data.code }
    }

    return { success: true }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Failed to update visibility' }
  }
}

// =============================================================================
// Subscription API
// =============================================================================

/**
 * Subscribe to a plan. Returns checkout URL if payment required.
 * Optionally bundles a one-time olive top-up (`extraOlives`) into the same
 * checkout and overrides the post-checkout return destination (`returnUrl`,
 * a relative path validated server-side).
 */
export async function subscribeToPlan(
  planId: string,
  orgId?: string,
  options?: { extraOlives?: number; returnUrl?: string; source?: string; promotionCode?: string },
): Promise<{ checkoutUrl?: string; success?: boolean; alreadySubscribed?: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/billing/subscribe`, {
    method: 'POST',
    body: JSON.stringify({
      planId,
      organizationId: orgId,
      ...(options?.extraOlives ? { extraOlives: options.extraOlives } : {}),
      ...(options?.returnUrl ? { returnUrl: options.returnUrl } : {}),
      // Attribution for topup_completed on bundled olives (mirrors createTopupCheckout)
      ...(options?.source ? { source: options.source } : {}),
      ...(options?.promotionCode ? { promotionCode: options.promotionCode } : {}),
    }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to subscribe')
  }

  return res.json()
}

/**
 * Confirm subscription after checkout redirect.
 */
export async function confirmSubscription(
  orgId?: string,
  promoSlug?: string,
): Promise<{ confirmed: boolean; plan?: string; status?: string; promoBonus?: number }> {
  const res = await fetchWithAuth(`${apiUrl()}/billing/subscribe/confirm`, {
    method: 'POST',
    body: JSON.stringify({ organizationId: orgId, promoSlug }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to confirm subscription')
  }

  return res.json()
}

/**
 * Cancel the current subscription (end-of-period).
 */
export interface SubscriptionCancellationSurvey {
  reasons: string[]
  feedback?: string
}

export async function cancelSubscription(
  orgId?: string,
  survey?: SubscriptionCancellationSurvey,
): Promise<{ success: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/billing/cancel-subscription`, {
    method: 'POST',
    body: JSON.stringify({ organizationId: orgId, survey }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to cancel subscription')
  }

  return res.json()
}

/**
 * Reverse a pending end-of-period subscription cancellation.
 */
export async function uncancelSubscription(orgId?: string): Promise<{ success: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/billing/uncancel-subscription`, {
    method: 'POST',
    body: JSON.stringify({ organizationId: orgId }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to resume subscription')
  }

  return res.json()
}

async function readTeamCheckoutResponse<T>(response: Response, fallback: string): Promise<T> {
  let data: unknown
  try {
    data = await response.json()
  } catch (error) {
    captureSentryError(error, { operation: 'team_checkout_response', extra: { status: response.status } })
    throw new Error(fallback)
  }
  if (!data || typeof data !== 'object') throw new Error(fallback)
  if (!response.ok) {
    const message = 'error' in data && typeof data.error === 'string' ? data.error : fallback
    // The status rides on the instance so a caller can tell an outage (5xx) from a rejected code (4xx).
    throw Object.assign(new Error(message || fallback), { status: response.status })
  }
  return data as T
}

export async function previewTeamCheckout(planId: string, promotionCode?: string): Promise<TeamCheckoutQuote> {
  const endpoint = '/teams/checkout/preview'
  const res = await fetchWithAuth(`${apiUrl()}${endpoint}`, {
    method: 'POST',
    body: JSON.stringify({ planId, promotionCode }),
  })
  // This handler has no resource-level 404: only an older backend lacks it.
  if (res.status === 404) throw new BackendRouteUnavailableError(endpoint)
  return readTeamCheckoutResponse(res, 'We could not check this price. Please try again.')
}

export async function previewPlanCheckout(planId: string, promotionCode?: string): Promise<TeamCheckoutQuote> {
  const endpoint = '/billing/subscribe/preview'
  const res = await fetchWithAuth(`${apiUrl()}${endpoint}`, {
    method: 'POST',
    body: JSON.stringify({ planId, promotionCode }),
  })
  if (res.status === 404) throw new BackendRouteUnavailableError(endpoint)
  return readTeamCheckoutResponse(res, 'We could not check this price. Please try again.')
}

/**
 * Create a team with checkout flow.
 */
export async function createTeamCheckout(
  name: string,
  planId: string,
  options?: { promotionCode?: string },
): Promise<{ checkoutUrl?: string; teamId: string; teamSlug: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/checkout`, {
    method: 'POST',
    body: JSON.stringify({ name, planId, promotionCode: options?.promotionCode }),
  })
  return readTeamCheckoutResponse(res, 'Failed to create team. Please try again.')
}

/**
 * Activate a team after successful checkout.
 */
export async function activateTeam(teamId: string): Promise<{ success: boolean; teamSlug?: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/activate`, {
    method: 'POST',
    body: JSON.stringify({ teamId }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to activate team')
  }

  return res.json()
}

/**
 * Cancel a pending team after the user abandons checkout.
 */
export async function cancelPendingTeam(teamId: string): Promise<{ success: boolean }> {
  const res = await fetchWithAuth(`${apiUrl()}/teams/cancel`, {
    method: 'POST',
    body: JSON.stringify({ teamId }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to cancel team')
  }

  return res.json()
}

// =============================================================================
// Account Stats
// =============================================================================

export interface AccountStats {
  completedGenerations: number
  byCapability: Record<string, number>
}

export async function getAccountStats(): Promise<AccountStats> {
  const res = await fetchWithAuth(`${apiUrl()}/account/stats`)

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error || 'Failed to get account stats')
  }

  return res.json()
}

// =============================================================================
// Force Y.Doc Replace (recovery)
// =============================================================================

export interface ForceReplaceYdocResult {
  success: boolean
  mapsReplaced: number
  totalEntries: number
  mapCounts: Record<string, number>
  payloadBytes: number
  partykitResponse: unknown
  snapshotKey: string | null
}

/**
 * Replace a project's PartyKit ydoc with the caller's local Yjs state.
 * Backend gates the call by user email allowlist + project membership.
 */
export async function forceReplaceYdoc(
  projectId: string,
  payload: Record<string, Record<string, unknown>>,
): Promise<ForceReplaceYdocResult> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/force-ydoc-replace`, {
    method: 'POST',
    body: JSON.stringify({ payload }),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    const message = error.error || error.message || `Restore failed (HTTP ${res.status})`
    const err = new Error(message) as Error & {
      responseBody?: unknown
      httpStatus?: number
      partykitStatus?: number
    }
    err.responseBody = error
    err.httpStatus = res.status
    if (typeof error?.partykitStatus === 'number') {
      err.partykitStatus = error.partykitStatus
    }
    throw err
  }

  return res.json()
}

/**
 * Deep-copy the source project's set/character/placement Postgres rows into an
 * already-created target project (part of the deep-copy primitive, spec/46).
 * Returns the old->new id maps used to remap the captured Yjs document so the
 * copy owns its own rows instead of silently sharing the source's.
 */
export async function deepCopyProjectRows(
  targetProjectId: string,
  sourceProjectId: string,
  capturedYdoc: Uint8Array,
): Promise<DeepCopyIdMaps> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${targetProjectId}/deep-copy-rows`, {
    method: 'POST',
    body: JSON.stringify({ sourceProjectId, ydocBase64: uint8ToBase64(capturedYdoc) }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || 'The project could not be copied. Try again.')
  }
  return res.json()
}

// ============================================================================
// Project snapshots (version history) — spec/46
// ============================================================================

export interface ProjectSnapshotMeta {
  id: string
  name: string
  created_at: string
  created_by: string | null
  author?: ProjectSnapshotAuthor | null
  ydoc_size_bytes: number | null
  metadata: ProjectSnapshotMetadata
  /** Public URL of the viewport preview thumbnail, when one was captured. */
  thumbnailUrl?: string | null
}

export interface ProjectSnapshotAuthor {
  id: string
  alias: string
  color: string
}

export interface ProjectSnapshotAssetCounts {
  total?: number
  video?: number
  image?: number
  audio?: number
  set?: number
  other?: number
}

/** Canvas viewport captured at snapshot time, so a restored/previewed project opens at the same view. */
export interface SnapshotView {
  sceneId: string
  x: number
  y: number
  zoom: number
}

export type ProjectSnapshotMetadata = {
  description?: string
  setCount?: number
  characterCount?: number
  clipCount?: number
  assetCounts?: ProjectSnapshotAssetCounts
  /** Pan/zoom + active scene when the snapshot was created. */
  view?: SnapshotView
} & Record<string, unknown>

/** Create a named, frozen snapshot of a project from a captured binary ydoc. */
export async function createSnapshot(
  projectId: string,
  name: string,
  ydocBase64: string,
  metadata?: ProjectSnapshotMetadata,
  thumbnailBase64?: string,
): Promise<ProjectSnapshotMeta> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/snapshots`, {
    method: 'POST',
    body: JSON.stringify({ name, ydocBase64, metadata, thumbnailBase64 }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || `Failed to create snapshot (HTTP ${res.status})`)
  }
  return res.json()
}

/** List a project's snapshots (newest first). */
export async function listSnapshots(projectId: string): Promise<ProjectSnapshotMeta[]> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/snapshots`)
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || `Failed to list snapshots (HTTP ${res.status})`)
  }
  const data = (await res.json()) as { snapshots: ProjectSnapshotMeta[] }
  return data.snapshots ?? []
}

/** Soft-delete a snapshot (the R2 bundle is never deleted). */
export async function deleteSnapshot(projectId: string, snapshotId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/snapshots/${snapshotId}`, {
    method: 'DELETE',
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || `Failed to delete snapshot (HTTP ${res.status})`)
  }
}

/**
 * Restore a snapshot as a new, independent project. Returns the new project id,
 * the id maps, and the bundle's binary ydoc (base64) for the client to remap
 * and push over WebSocket.
 */
export async function restoreSnapshot(
  projectId: string,
  snapshotId: string,
  name?: string,
  preview?: boolean,
): Promise<{ projectId: string; idMaps: DeepCopyIdMaps; ydocBase64: string }> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/snapshots/${snapshotId}/restore`, {
    method: 'POST',
    body: JSON.stringify({ name, preview }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || `Failed to restore snapshot (HTTP ${res.status})`)
  }
  return res.json()
}

/** Keep a throwaway preview project ("Save as new project") — clears is_preview. */
export async function promotePreviewProject(
  projectId: string,
  teamId?: string | null,
  name?: string | null,
): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/promote-preview`, {
    method: 'POST',
    body: JSON.stringify({ teamId: teamId ?? null, name: name ?? null }),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || `Failed to keep project (HTTP ${res.status})`)
  }
}

/** Discard a throwaway preview project — hard delete (only ever a preview project). */
export async function discardPreviewProject(projectId: string): Promise<void> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/preview`, { method: 'DELETE' })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    throw new Error(error.error || error.message || `Failed to discard preview (HTTP ${res.status})`)
  }
}

// --- Learn/Docs "Ask AI" assistant ------------------------------------------

export interface DocsChatTurn {
  role: 'user' | 'assistant'
  content: string
}

/** Carries the backend error code (e.g. RATE_LIMITED) so the chat UI can show a localized message. */
export class DocsChatError extends Error {
  constructor(public readonly code: string) {
    super(code)
    this.name = 'DocsChatError'
  }
}

/**
 * Ask the docs assistant a question. The backend (POST /docs-chat/message)
 * answers from the bundled docs via fal's openrouter/router. Returns the reply text, or
 * throws a `DocsChatError` whose `code` the UI maps to a friendly message.
 */
export async function askDocsAssistant(messages: DocsChatTurn[], locale: AppLocale): Promise<string> {
  let res: Response
  try {
    res = await fetchWithAuth(`${apiUrl()}/docs-chat/message`, {
      method: 'POST',
      body: JSON.stringify({ messages, locale }),
    })
  } catch {
    throw new DocsChatError('NETWORK')
  }
  const data = await res.json().catch(() => ({}) as { reply?: string; error?: string })
  if (res.ok && typeof data.reply === 'string') return data.reply
  throw new DocsChatError(typeof data.error === 'string' ? data.error : `HTTP_${res.status}`)
}

// =============================================================================
// Comments (canvas + timeline review threads)
// =============================================================================
// All comment writes go through the backend (DB is the source of truth; the
// backend mirrors into the Yjs `comments` map for live delivery). The client
// generates the comment UUID so the optimistic pin can render immediately and
// retries stay idempotent.

export class ProjectCommentsRequestError extends Error {
  constructor(public readonly status: number) {
    super(`Failed to fetch comments (${status})`)
    this.name = 'ProjectCommentsRequestError'
  }
}

export async function fetchProjectComments(
  projectId: string,
  options: { signal?: AbortSignal } = {},
): Promise<CommentRecord[]> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/comments`, {
    signal: options.signal,
  })
  if (!res.ok) throw new ProjectCommentsRequestError(res.status)
  const data = (await res.json()) as { comments: CommentRecord[] }
  return data.comments
}

/**
 * The backend rejected the comment for a reason the user can fix by editing it
 * (unknown reference kind, too many references). `message` is backend-authored
 * user copy, safe to render; retrying the same text can never succeed.
 */
export class CommentRejectedError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'CommentRejectedError'
  }
}

export async function createProjectComment(projectId: string, input: CreateCommentRequest): Promise<CommentRecord> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/comments`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
  const data = (await res.json().catch(() => ({}))) as { comment?: CommentRecord; error?: string; message?: string }
  if (!res.ok || !data.comment) {
    if (data.message) throw new CommentRejectedError(data.error ?? 'rejected', data.message)
    throw new Error(data.error ?? `Failed to post comment (${res.status})`)
  }
  return data.comment
}

export async function resolveProjectCommentThread(
  projectId: string,
  threadId: string,
  resolved: boolean,
): Promise<CommentRecord> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/comments/${threadId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolved }),
  })
  const data = (await res.json().catch(() => ({}))) as { comment?: CommentRecord; error?: string }
  if (!res.ok || !data.comment) throw new Error(data.error ?? `Failed to resolve thread (${res.status})`)
  return data.comment
}

export async function moveProjectComment(
  projectId: string,
  commentId: string,
  anchor: CommentRecord['anchor'],
): Promise<CommentRecord> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/comments/${commentId}/anchor`, {
    method: 'POST',
    body: JSON.stringify({ anchor }),
  })
  const data = (await res.json().catch(() => ({}))) as { comment?: CommentRecord; error?: string }
  if (!res.ok || !data.comment) throw new Error(data.error ?? `Failed to move comment (${res.status})`)
  return data.comment
}

export async function deleteProjectComment(projectId: string, commentId: string): Promise<CommentRecord> {
  const res = await fetchWithAuth(`${apiUrl()}/projects/${projectId}/comments/${commentId}`, {
    method: 'DELETE',
  })
  const data = (await res.json().catch(() => ({}))) as { comment?: CommentRecord; error?: string }
  if (!res.ok || !data.comment) throw new Error(data.error ?? `Failed to delete comment (${res.status})`)
  return data.comment
}

async function colorLabelNamesRequest(
  path: string,
  patch?: Partial<Record<ShotColorLabel, string | null>>,
): Promise<{ colorLabelNames: ColorLabelNames }> {
  const response = await fetchWithAuth(
    `${apiUrl()}${path}`,
    patch === undefined
      ? undefined
      : {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ colorLabelNames: patch }),
        },
  )
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new Error(
      typeof body?.error === 'string' ? body.error : 'Label names could not be loaded or saved. Try again.',
    )
  }
  return response.json()
}

export function getOrganizationColorLabelNames(orgId: string) {
  return colorLabelNamesRequest(`/organizations/${orgId}/color-label-names`)
}

export function updateOrganizationColorLabelNames(
  orgId: string,
  names: Partial<Record<ShotColorLabel, string | null>>,
) {
  return colorLabelNamesRequest(`/organizations/${orgId}/color-label-names`, names)
}

export function getProjectColorLabelNames(projectId: string) {
  return colorLabelNamesRequest(`/projects/${projectId}/color-label-names`)
}

export interface SeedanceDraftEnhancementQuote {
  modelId: string
  sourceAssetId: string
  durationSeconds: number
  enhanceUntil: string
  canEnhance: boolean
  estimatedOlives: number
  expectedPricing: { ruleId: string | null; ruleVersion: number | null }
}
export async function fetchSeedanceDraftEnhancementQuote(
  projectId: string,
  sourceAssetId: string,
): Promise<SeedanceDraftEnhancementQuote> {
  const res = await fetchWithAuth(
    `${apiUrl()}/projects/${encodeURIComponent(projectId)}/assets/${encodeURIComponent(sourceAssetId)}/draft-enhancement`,
  )
  const body = await res.json()
  if (!res.ok) throw new Error(body.error || 'The enhancement price is unavailable. Please try again.')
  return body
}
